import { mapRawTodos, buildOutlineData as buildPlanData, augmentMessageComponents, normalizeSourceUrl } from './utils';

/** Split an SDK MCP tool name into its server namespace and native tool name. */
export const parseMcpToolName = (name) => {
  if (typeof name !== 'string' || !name) {
    return { rawName: name, server: null, tool: name };
  }

  const match = name.match(/^mcp__(.+?)__(.+)$/);
  if (!match) {
    return { rawName: name, server: null, tool: name };
  }

  return {
    rawName: name,
    server: match[1],
    tool: match[2]
  };
};

/** Strip any MCP server prefix while preserving non-MCP tool names. */
export const normalizeMcpToolName = (name) => parseMcpToolName(name).tool;

const CONNECTED_APPS = new Set(['excel', 'powerpoint', 'word']);

/**
 * Resolve a proxied Office tool to the connected app that will execute it.
 * The preferred contract is mcp__connected_<app>__<tool>. Two alternate
 * spellings are accepted so the UI remains compatible with a shared proxy
 * server and with already-normalized SDK telemetry.
 */
export const resolveConnectedAppTool = (name) => {
  const parsed = parseMcpToolName(name);
  const serverMatch = parsed.server?.match(/^connected_(excel|powerpoint|word)$/);

  if (serverMatch) {
    return {
      app: serverMatch[1],
      server: parsed.server,
      tool: parsed.tool,
      rawName: parsed.rawName
    };
  }

  if (parsed.server === 'office_proxy') {
    const proxyMatch = parsed.tool?.match(/^(excel|powerpoint|word)_(.+)$/);
    if (proxyMatch && CONNECTED_APPS.has(proxyMatch[1])) {
      return {
        app: proxyMatch[1],
        server: parsed.server,
        tool: proxyMatch[2],
        rawName: parsed.rawName
      };
    }
  }

  const normalizedMatch = parsed.tool?.match(/^connected_(excel|powerpoint|word)_(.+)$/);
  if (normalizedMatch && CONNECTED_APPS.has(normalizedMatch[1])) {
    return {
      app: normalizedMatch[1],
      server: parsed.server,
      tool: normalizedMatch[2],
      rawName: parsed.rawName
    };
  }

  return null;
};

/**
 * Defensive slide_index extractor. Tools are inconsistent: OOXML tools usually
 * pass `slide_index` (0-based), some pass `slideNumber` (1-based), and the
 * legacy frontend code reads `slideIndex`. Try every shape the backend might
 * emit so the unified slideWork card can count distinct slides regardless.
 */
const extractSlideIndex = (args) => {
  if (Number.isFinite(args?.slide_index)) return args.slide_index;
  if (Number.isFinite(args?.slideIndex)) return args.slideIndex;
  if (Number.isFinite(args?.slideNumber)) return args.slideNumber - 1;
  return null;
};

/**
 * Build placeholder slides for a createSlides card using ordinal labels.
 * Deck positions are intentionally NOT predicted — Office.js decides where slides
 * actually land and the frontend has no way to know until after execution.
 * Ordinals ("Slide 1 of 5") are always correct and never mislead the user.
 */
const buildCreateSlidePlaceholders = (count, toolId) => {
  const safeCount = Math.max(1, Number.isFinite(count) ? count : 1);

  return Array.from({ length: safeCount }, (_, index) => {
    const ordinal = index + 1;
    return {
      id: `placeholder-${toolId}-${index}`,
      slideIndex: index,
      slideNumber: ordinal,
      displayNumber: null,
      ordinal,
      totalCount: safeCount,
      title: `Slide ${ordinal} of ${safeCount}`,
      completed: false,
      inProgress: index === 0,
      isPlaceholder: true
    };
  });
};

/** Friendly labels for all tools (MCP + legacy). */
const MCP_TOOL_LABELS = {
  execute_office_js: 'Modifying slide',
  edit_slide_xml: 'Editing slide',
  edit_slide_chart: 'Creating chart',
  edit_slide_master: 'Editing theme',
  read_slide_text: 'Reading text',
  edit_slide_text: 'Editing text',
  screenshot_slide: 'Taking screenshot',
  verify_slides: 'Verifying layout',
  verify_slide_visual: 'Visual design review',
  ask_user_question: 'Waiting for your answer',
  search_icons: 'Searching icons',
  insert_icon: 'Inserting icon',
  read_deck_content: 'Reading slides',
  list_slide_shapes: 'Listing shapes',
  duplicate_slide: 'Duplicating slide',
  web_search: 'Researching',
  // Knowledge Locker. Retrieval is the Locker's entire product surface, so a
  // turn that consulted the firm's own documents has to say so: without these
  // the four tools fell through to the default case and three successful
  // searches rendered as nothing at all between Thinking and the answer.
  // They arrive as mcp__locker__<tool> and are matched after the single strip
  // parseMcpToolName performs.
  search_locker: 'Searching firm knowledge',
  find_locker_exact: 'Finding exact match',
  list_locker_documents: 'Listing firm documents',
  fetch_locker_citations: 'Reading firm sources',
  // Excel. Every one of these rendered as nothing at all before: the switch
  // below had no case for any of them, so a turn that built three sheets and
  // wrote forty formulas showed the user one mislabeled card. Names taken from
  // createExcelMcpServer, not guessed. Strings match CONNECTED_TOOL_LABELS
  // where the same tool is also reachable through the cross-app proxy, so one
  // operation reads the same whichever pane is driving it.
  get_initial_state: 'Inspecting the open file',
  get_cell_ranges: 'Reading cells',
  get_range_as_csv: 'Reading range',
  search_data: 'Searching workbook',
  screenshot_range: 'Reviewing worksheet',
  get_all_objects: 'Inspecting workbook objects',
  set_cell_range: 'Updating cells',
  clear_cell_range: 'Clearing cells',
  copy_to: 'Copying cells',
  modify_sheet_structure: 'Updating worksheet structure',
  modify_workbook_structure: 'Updating workbook structure',
  resize_range: 'Resizing rows and columns',
  modify_object: 'Updating charts and pivots',
  // Word. Same gap, same fix.
  edit_doc_text: 'Updating document text',
  edit_doc_list: 'Updating document list',
  read_doc_section: 'Reading document section',
  collapse_blank_paragraphs: 'Cleaning document spacing',
  verify_doc: 'Verifying document',
  screenshot_document: 'Reviewing document',
  propose_doc_edits: 'Preparing edits for review',
  explain_edits: 'Checking the edits',
  // Shared across all three hosts.
  apply_template: 'Applying template',
  read_skill: 'Reading workflow',
  // Legacy
  createSlides: 'Creating slides',
  editSlide: 'Editing slide',
  WebSearch: 'Researching',
  readSlides: 'Reading slides',
  listSlides: 'Listing slides',
  updateManifest: 'Updating presentation',
  readPresentation: 'Reading presentation'
};

/**
 * The fallback label for `execute_office_js`, per Office host.
 *
 * It is the only tool all three hosts share, and this string is the whole card
 * whenever the model sends no `explanation`. "Modifying slide" on a workbook
 * was the single card the reported Excel turn rendered for twenty operations.
 */
const OFFICE_JS_LABEL_BY_APP = {
  excel: 'Modifying the workbook',
  word: 'Modifying the document'
};

const officeJsLabel = (app) => OFFICE_JS_LABEL_BY_APP[app] || MCP_TOOL_LABELS.execute_office_js;

const CONNECTED_TOOL_LABELS = {
  get_initial_state: 'Inspecting the open file',
  get_cell_ranges: 'Reading cells',
  get_range_as_csv: 'Reading range',
  search_data: 'Searching workbook',
  screenshot_range: 'Reviewing worksheet',
  get_all_objects: 'Inspecting workbook objects',
  set_cell_range: 'Updating cells',
  clear_cell_range: 'Clearing cells',
  copy_to: 'Copying cells',
  modify_sheet_structure: 'Updating worksheet structure',
  modify_workbook_structure: 'Updating workbook structure',
  resize_range: 'Resizing range',
  modify_object: 'Updating workbook',
  edit_doc_text: 'Updating document text',
  edit_doc_list: 'Updating document list',
  read_doc_section: 'Reading document section',
  collapse_blank_paragraphs: 'Cleaning document spacing',
  verify_doc: 'Verifying document',
  screenshot_document: 'Reviewing document',
  read_deck_content: 'Reading slides',
  read_slide_text: 'Reading slide text',
  list_slide_shapes: 'Inspecting slide layout',
  screenshot_slide: 'Reviewing slide',
  execute_office_js: 'Updating the open file',
  edit_slide_xml: 'Updating slide',
  edit_slide_chart: 'Updating chart',
  edit_slide_text: 'Updating slide text',
  edit_slide_master: 'Updating presentation theme',
  duplicate_slide: 'Duplicating slide',
  verify_slides: 'Verifying slides',
  verify_slide_visual: 'Reviewing slide design',
  apply_template: 'Applying presentation template',
  search_icons: 'Finding presentation icons',
  insert_icon: 'Adding presentation icon'
};

const humanizeToolName = (name) => {
  if (typeof name !== 'string' || !name) return 'Working in the file';
  const words = name
    .replace(/^(get|read)_/, 'Reading ')
    .replace(/^search_/, 'Searching ')
    .replace(/^(set|edit|modify|apply|update)_/, 'Updating ')
    .replace(/^create_/, 'Creating ')
    .replace(/^insert_/, 'Inserting ')
    .replace(/^verify_/, 'Verifying ')
    .replace(/_/g, ' ')
    .trim();

  if (!words) return 'Working in the file';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const connectedToolDisplayLabel = (toolArgs, connectedTool) => {
  const explanation = typeof toolArgs?.explanation === 'string'
    ? toolArgs.explanation.trim()
    : '';
  if (explanation) return explanation.slice(0, 80);
  if (connectedTool?.tool === 'get_initial_state') {
    return {
      excel: 'Inspecting workbook',
      powerpoint: 'Inspecting presentation',
      word: 'Inspecting document'
    }[connectedTool.app] || 'Inspecting file';
  }
  return CONNECTED_TOOL_LABELS[connectedTool?.tool] || humanizeToolName(connectedTool?.tool);
};

/**
 * Display label for a tool call. Prefers the model's per-call `explanation`
 * argument (the same 50-char rationale Claude for PowerPoint surfaces on its
 * tool cards) so the user sees WHAT the agent is doing on THIS call, not just
 * the generic verb for the tool. Falls back to the static label.
 */
const toolDisplayLabel = (toolArgs, toolName, fallback) => {
  const explanation = typeof toolArgs?.explanation === 'string' ? toolArgs.explanation.trim() : '';
  if (explanation) return explanation.slice(0, 60);
  return MCP_TOOL_LABELS[toolName] || fallback;
};

const realSlidesCount = (slides = []) => {
  if (!Array.isArray(slides) || slides.length === 0) {
    return 0;
  }
  return slides.reduce((count, slide) => count + (slide && slide.isPlaceholder ? 0 : 1), 0);
};

// =============================================================================
// CONSOLIDATED STREAM HANDLERS
// Combines: status.js, slides.js, event-handlers.js, componentUpdaters.js
// Total: ~1,550 lines consolidated into single file
// =============================================================================


// =============================================================================
// SECTION 1: STATUS HANDLERS
// Original file: status.js (146 lines)
// =============================================================================

/**
 * Freeze every timer component on a turn that has just ended.
 *
 * EVERY terminal branch has to call this, not just the happy one. The pane
 * decides whether the elapsed count keeps moving from the timer component and
 * the message metadata together, so a terminal path that settles only one of
 * them (or neither, which is what the credits and rate-limit branches used to
 * do) leaves a finished turn counting upward until the chat is reloaded.
 *
 * `completedAt` is the anchor the renderer pins the count to, so it is always
 * written, even when the backend gave us no duration to write with it.
 */
const settleTimerComponents = (components, { completedAt, durationSeconds = null } = {}) => {
  if (!Array.isArray(components)) return components;
  const settledAt = completedAt || new Date().toISOString();

  return components.map((component) => {
    if (component?.type !== 'timer') return component;

    const startMs = Date.parse(component.data?.startTime);
    const endMs = Date.parse(settledAt);
    const measured = Number.isFinite(startMs) && Number.isFinite(endMs)
      ? Math.max(0, Math.floor((endMs - startMs) / 1000))
      : null;
    const resolvedDuration = typeof durationSeconds === 'number'
      ? durationSeconds
      : (measured ?? component.data?.elapsedSeconds ?? 0);

    return {
      ...component,
      data: {
        ...component.data,
        isStreaming: false,
        isComplete: true,
        isActive: false,
        durationSeconds: resolvedDuration,
        elapsedSeconds: resolvedDuration,
        completedAt: component.data?.completedAt || settledAt
      }
    };
  });
};

const researchStatusPatterns = [
  { pattern: /Searching for relevant information/i, status: 'Searching for relevant information' },
  { pattern: /Analyzing sources and generating report/i, status: 'Analyzing sources and generating report' }
];

export const handleStatusEvent = (event, state, componentUpdaters, context = {}) => {
  console.log('ℹ️ Processing status event:', {
    messageLength: typeof event.message === 'string' ? event.message.length : 0,
    reason: event.reason,
    stage: event.stage,
    allKeys: Object.keys(event)
  });

  // Plan metadata is no longer used (plan mode removed) but kept for backwards compat
  const planMetadata = {};

  // Handle heartbeat events - keep-alive updates for long-running tools
  // Match by tool name since SDK doesn't pass toolCallId to tool handlers
  if (event.stage === 'heartbeat' && event.tool) {
    console.log('💓 Heartbeat event:', { tool: event.tool, timestamp: event.timestamp });
    return componentUpdaters.updateTaskHeartbeat(state, event.tool, event.timestamp);
  }

  // Handle credits exhaustion - show rate limit card and complete stream
  const isCreditLimitEvent = event.stage === 'rate_limit' ||
    event.reason === 'credits_exhausted' ||
    event.reason === 'allowance_exhausted' ||
    event.reason === 'rate_limit_exceeded';

  if (isCreditLimitEvent) {
    console.log('💰 Credit limit hit - adding rate limit component and completing stream', {
      reason: event.reason,
      throttleUntil: event.throttleUntil,
      plan: event.plan,
      creditsRemaining: event.creditsRemaining,
      creditsRequired: event.creditsRequired
    });

    // Mark all in-progress components as complete
    let components = componentUpdaters.markThinkingComplete(state.components);
    components = componentUpdaters.markAllComponentsComplete(components);
    components = componentUpdaters.settleConnectedAppWorkComponents(components, false);
    const creditLimitCompletedAt = new Date().toISOString();
    components = settleTimerComponents(components, { completedAt: creditLimitCompletedAt });

    // Add rate limit card. adminFirstName personalizes the "ask your admin"
    // copy (a name, never a number — the adoption guard holds).
    components = componentUpdaters.addRateLimitComponent(components, {
      creditsRemaining: typeof event.creditsRemaining === 'number' ? event.creditsRemaining : 0,
      creditsRequired: typeof event.creditsRequired === 'number' ? event.creditsRequired : null,
      resetsAt: event.creditsExpiry || event.throttleUntil,
      plan: event.plan,
      message: event.message,
      adminFirstName: event.adminFirstName || null
    });

    return {
      ...state,
      components,
      aiMessageId: state.aiMessageId || `ai-${Date.now()}`,
      isStreaming: false, // Mark stream as complete
      isComplete: true,   // Mark message as complete
      metadata: {
        ...state.metadata,
        ...planMetadata,
        // createMessage reads the message's streaming state from metadata, so
        // stopping the stream without these three left the pane rendering a
        // finished turn as a live one, elapsed count still climbing.
        isStreaming: false,
        isComplete: true,
        completedAt: creditLimitCompletedAt,
        statusLabel: null,
        statusHint: null
      }
    };
  }

  if (event.stage === 'credits_update') {
    if (typeof context?.onCreditsUpdate === 'function') {
      try {
        context.onCreditsUpdate(event);
      } catch (error) {
        console.warn('⚠️ Failed to deliver credits update to context:', error);
      }
    }

    const creditsMetadata = {
      creditsRemaining: typeof event.creditsRemaining === 'number'
        ? event.creditsRemaining
        : event.remainingCredits ?? null,
      creditsConsumed: event.creditsConsumed ?? null,
      deltaCostUSD: event.deltaCostUSD ?? event.costUSD ?? null,
      updatedAt: event.updatedAt || event.timestamp || new Date().toISOString()
    };

    return {
      ...state,
      metadata: {
        ...state.metadata,
        ...planMetadata,
        latestCredits: creditsMetadata
      }
    };
  }

  if (event.stage === 'completion_guard') {
    const statusTimestamp = event.lastStatusChange || new Date().toISOString();

    // Simplified guard state - backend only sends {status, reason}
    const guard = {
      status: event.guardStatus || 'blocked',
      satisfied: event.guardStatus === 'satisfied',
      blocked: event.guardStatus === 'blocked',
      reason: event.reason || null,
      lastStatusChange: statusTimestamp
    };

    return {
      ...state,
      metadata: {
        ...state.metadata,
        ...planMetadata,
        guard,
        statusHint: event.message || state.metadata?.statusHint || null
      }
    };
  }

  // Handle compaction status events (from PreCompact hook and compact_boundary)
  if (event.stage === 'compacting' || event.stage === 'compacting_complete') {
    const isCompacting = event.stage === 'compacting';

    console.log('🗜️ Compaction status:', {
      stage: event.stage,
      isCompacting,
      trigger: event.trigger
    });

    // Notify context about compaction state change
    if (context?.onCompactionStateChange) {
      context.onCompactionStateChange(isCompacting, event);
    }

    return {
      ...state,
      metadata: {
        ...state.metadata,
        ...planMetadata,
        compaction: {
          isCompacting,
          trigger: event.trigger || 'auto',
          ...(event.contextManagement || {}),
          timestamp: new Date().toISOString()
        },
        statusHint: isCompacting ? 'Compacting context...' : null
      }
    };
  }

  // If no message, return unchanged state (after handling special cases above)
  if (!event.message) {
    console.log('⚠️ Status event has no message and no special handling applied');
    return state;
  }

  // Research status patterns are now handled via TodoChecklist with SDK telemetry
  // No longer need to update citations component directly - just emit statusHint
  for (const { pattern, status } of researchStatusPatterns) {
    if (pattern.test(event.message)) {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...planMetadata,
          statusHint: status
        }
      };
    }
  }

  // Allow SDK hook messages (with stage metadata) to pass through
  const isSDKHookMessage = event.stage === 'tool_start' ||
                          event.stage === 'tool_complete' ||
                          event.source === 'sdk_hook';

  // P0 FIX: Mark thinking complete when Write/Edit/MultiEdit/MCP tool starts
  // This ensures thinking doesn't keep running while slides are being created
  const slideModifyingTools = [
    'Write', 'Edit', 'MultiEdit',
    'execute_office_js', 'edit_slide_xml', 'edit_slide_chart',
    'edit_slide_master', 'edit_slide_text', 'duplicate_slide'
  ];
  const normalizedEventTool = normalizeMcpToolName(event.tool);
  if (event.stage === 'tool_start' && slideModifyingTools.includes(normalizedEventTool)) {
    console.log('🎯 Slide-modifying tool started, marking thinking complete:', event.tool);
    const componentsWithThinkingComplete = componentUpdaters.markThinkingComplete(state.components);

    return {
      ...state,
      components: componentsWithThinkingComplete,
      metadata: {
        ...state.metadata,
        ...planMetadata
      }
    };
  }

  // A refused turn. The server declined before or during the run and explains
  // itself here; the turn_complete that follows within milliseconds is what
  // actually ends the turn, so this branch only records the reason and leaves
  // completion alone. Without the card the explanation would land in the
  // transient statusLabel below and be wiped by that completion, leaving a
  // refusal indistinguishable from a turn that simply said nothing.
  if (event.stage === 'turn_error') {
    console.log('⛔ Turn error event received:', {
      code: event.code,
      reason: event.reason,
      messageLength: typeof event.message === 'string' ? event.message.length : 0
    });

    let components = componentUpdaters.markThinkingComplete(state.components);
    components = componentUpdaters.markAllComponentsComplete(components);
    components = componentUpdaters.settleConnectedAppWorkComponents(components, false);
    components = componentUpdaters.addTurnErrorComponent(components, {
      message: typeof event.message === 'string' && event.message.trim()
        ? event.message.trim()
        : null,
      code: event.code || event.reason || null
    });

    return {
      ...state,
      components,
      metadata: {
        ...state.metadata,
        ...planMetadata,
        statusLabel: null,
        statusHint: null
      }
    };
  }

  // Handle turn completion events with authoritative completion signal.
  // Sessions are persistent — we only complete turns, not sessions.
  // - turn_complete: SDK ResultMessage received (AI finished responding)
  // - turn_stopped / turn_interrupted: user interrupted the request
  const isCompletionEvent =
    (event.stage === 'turn_complete') ||
    (event.stage === 'turn_stopped') ||
    (event.stage === 'turn_interrupted');

  if (isCompletionEvent) {
    console.log('✅ Turn completion event received:', {
      stage: event.stage,
      reason: event.reason,
      durationSeconds: event.durationSeconds,
      duration_ms: event.duration_ms,
      completedAt: event.completedAt,
      isCancelled: event.isCancelled
    });

    // Maximum display cap to prevent unrealistic durations (10 minutes)
    const MAX_DURATION_SECONDS = 10 * 60;

    // Calculate backend duration with cap
    let backendDuration = null;
    if (typeof event.durationSeconds === 'number') {
      backendDuration = Math.min(event.durationSeconds, MAX_DURATION_SECONDS);
    } else if (typeof event.duration_ms === 'number') {
      backendDuration = Math.min(Math.floor(event.duration_ms / 1000), MAX_DURATION_SECONDS);
    }

    // Fallback: calculate from startTime if backend didn't provide duration
    if (backendDuration === null && state.metadata?.startTime) {
      const startMs = typeof state.metadata.startTime === 'number'
        ? state.metadata.startTime
        : Date.parse(state.metadata.startTime);
      if (!Number.isNaN(startMs)) {
        backendDuration = Math.min(
          Math.max(0, Math.floor((Date.now() - startMs) / 1000)),
          MAX_DURATION_SECONDS
        );
      }
    }

    // Final fallback
    const durationSeconds = backendDuration ?? state.metadata?.durationSeconds ?? 0;
    const completedAt = event.completedAt || new Date().toISOString();

    // Finalize any in-progress research report capture
    let components = componentUpdaters.finalizeResearchReport(state.components);

    // Mark all components complete and update timer with backend duration
    components = componentUpdaters.markThinkingComplete(components);
    components = componentUpdaters.markAllComponentsComplete(components);
    components = componentUpdaters.settleConnectedAppWorkComponents(
      components,
      event.stage === 'turn_complete'
    );

    // Update timer component with backend-authoritative duration
    components = settleTimerComponents(components, { completedAt, durationSeconds });

    return {
      ...state,
      components,
      isStreaming: false,
      metadata: {
        ...state.metadata,
        ...planMetadata,
        isStreaming: false,
        isComplete: true,
        // Defensive cleanup: if a research sub-agent was still marked active
        // when the turn ended (e.g., the sub-agent crashed without emitting
        // subagent_complete), clear the flag so the next turn starts fresh.
        activeResearchAgentId: null,
        durationSeconds,
        completedAt,
        statusLabel: event.stage === 'turn_stopped' ? 'Request cancelled' : 'Request processed',
        statusHint: null
      }
    };
  }

  if (event.message.match(/^Executing\s+.+\.\.\./)) {
    if (!isSDKHookMessage) {
      console.log('⏭️ Skipping tool execution message');
      return state;
    }
    console.log('✅ Allowing SDK hook message through', {
      stage: event.stage,
      source: event.source,
    });
  }

  let message = event.message;
  if (event.message.includes('comprehensive review') || event.message.includes('fact-checking') ||
      event.message.includes('Updating deliverable format') || event.message.includes('Re-analyzing')) {
    message = 'Reviewing';
  } else if (event.message.includes('Output approved') || event.message.includes('approved')) {
    message = 'Approved';
  } else if (event.message.includes('Applying corrections') || event.message.includes('Adjusting approach')) {
    message = 'Adjusting';
  }

  return {
    ...state,
    metadata: {
      ...state.metadata,
      ...planMetadata,
      statusLabel: message,
      statusHint: null
    }
  };
};

// =============================================================================
// SECTION 2: SLIDE HANDLERS
// Original file: slides.js (193 lines)
// =============================================================================

export const handleSlidesEvent = async (event, state, context, componentUpdaters) => {
  if (!event.slides || event.slides.length === 0) return state;

  // CRITICAL FIX: Detect if this is an edit operation
  // Edits have changes with textBefore (showing what was replaced)
  // Creations only have textAfter (showing what was added)
  const hasEditChanges = event.slides.some(slide =>
    Array.isArray(slide.changes) &&
    slide.changes.some(change => change.textBefore && change.textBefore.trim().length > 0)
  );
  const isEdit = hasEditChanges;

  console.log('🛠️ Processing slides event', {
    slideCount: event.slides.length,
    isEdit,
    hasContentPreview: event.slides.some(s => s.contentPreview),
  });

  let components = componentUpdaters.markThinkingComplete(state.components);

  // Route to correct card based on operation type
  if (isEdit) {
    // This is an edit operation - route to SlideEditCard
    const editedSlides = event.slides.map((slide, index) => {
      const explicitSlideNumber = Number.isFinite(slide.displayNumber)
        ? slide.displayNumber
        : (Number.isFinite(slide.slideNumber) ? slide.slideNumber : null);
      const resolvedIndex = Number.isFinite(slide.index)
        ? slide.index
        : (Number.isFinite(slide.slideIndex) ? slide.slideIndex : null);
      const slideNumber = explicitSlideNumber ?? (Number.isFinite(resolvedIndex) ? resolvedIndex + 1 : index + 1);

      return {
        slideNumber,
        slideIndex: Number.isFinite(resolvedIndex) ? resolvedIndex : slideNumber - 1,
        displayNumber: slideNumber,
        slideId: slide.id || slide.slideId || `slide-${slideNumber}`,
        slideTitle: slide.title || slide.slideTitle || `Slide ${slideNumber}`,
        changes: slide.changes || []
      };
    });

    components = componentUpdaters.updateSlideEditComponent(
      components,
      null,
      null,
      false,
      editedSlides,
      event.slides.length,
      event.slides.length,
      null  // toolId - not available in slides event
    );
  } else {
    // This is a creation operation - route to SlideCreationCard
    components = componentUpdaters.updateSlideCreationComponent(
      components,
      `Received ${event.slides.length} slide${event.slides.length === 1 ? '' : 's'}`,
      event.slides.length,
      false,
      null,
      event.slides.map(s => s.id),
      event.slides.map(s => s.index ?? 0),
      event.slides.length,
      [],
      null  // toolId - not available in slides event
    );
  }

  components = componentUpdaters.updateTextComponent(components, event.summary || '', true);

  const duration = state.metadata?.startTime
    ? `${Math.floor((Date.now() - state.metadata.startTime) / 60000)}m ${Math.floor(((Date.now() - state.metadata.startTime) % 60000) / 1000)}s`
    : null;

  if (!isEdit) {
    // Only update creation card with final status if this is a creation operation
    const completedProgressSlides = event.slides.map((slide, index) => {
      const explicitSlideNumber = Number.isFinite(slide.displayNumber)
        ? slide.displayNumber
        : (Number.isFinite(slide.slideNumber) ? slide.slideNumber : null);
      const resolvedIndex = Number.isFinite(slide.index)
        ? slide.index
        : (Number.isFinite(slide.slideIndex) ? slide.slideIndex : null);
      const slideNumber = explicitSlideNumber ?? (Number.isFinite(resolvedIndex) ? resolvedIndex + 1 : index + 1);

      return {
        id: slide.id,
        slideId: slide.id,
        slideIndex: Number.isFinite(resolvedIndex) ? resolvedIndex : slideNumber - 1,
        displayNumber: slideNumber,
        slideNumber,
        title: slide.title,
        completed: true,
        inProgress: false,  // All slides are completed when this event fires
        contentPreview: slide.contentPreview || null  // Include content preview for expandable UI
      };
    });

    components = componentUpdaters.updateSlideCreationComponent(
      components,
      `Created ${event.slides.length} slide${event.slides.length > 1 ? 's' : ''}`,
      event.slides.length,
      false,
      duration,
      event.slides.map(s => s.id),
      event.slides.map(s => s.index ?? 0),
      event.slides.length,
      completedProgressSlides,
      null  // toolId - not available
    );
  }

  return {
    ...state,
    components,
    metadata: {
      ...state.metadata,
      isCreatingSlides: false,
      isEditingSlides: false,
      statusLabel: isEdit
        ? `Updated ${event.slides.length} slide${event.slides.length === 1 ? '' : 's'}`
        : `Created ${event.slides.length} slide${event.slides.length === 1 ? '' : 's'}`,
      statusHint: null
    }
  };
};

export const handleSlideProgressEvent = (event, state, componentUpdaters) => {
  if (event.slideIndex === undefined) return state;

  console.log('📊 Processing slide progress event:', {
    slideIndex: event.slideIndex,
    totalSlides: event.totalSlides,
    stage: event.stage,
  });

  let componentsState = componentUpdaters.markThinkingComplete(state.components);
  let slideCreationIndex = componentsState.findIndex(c => c.type === 'createSlides');

  if (slideCreationIndex === -1) {
    const inferredTotal = event.totalSlides || state.metadata?.totalSlidesToCreate || 1;
    const bootstrapSlides = Array.from({ length: inferredTotal }, (_, index) => {
      const ordinal = index + 1;
      return {
        completed: false,
        inProgress: index === 0,
        slideIndex: index,
        slideNumber: ordinal,
        displayNumber: null,
        ordinal,
        totalCount: inferredTotal,
        title: `Slide ${ordinal} of ${inferredTotal}`,
        isPlaceholder: true
      };
    });

    componentsState = componentUpdaters.updateSlideCreationComponent(
      componentsState,
      `Creating slides... 0/${inferredTotal} completed`,
      inferredTotal,
      true,
      null,
      [],
      [],
      0,
      bootstrapSlides,
      null  // toolId - not available
    );

    slideCreationIndex = componentsState.findIndex(c => c.type === 'createSlides');
  }

  if (slideCreationIndex === -1) {
    return state;
  }

  const slideCreationComponent = componentsState[slideCreationIndex];
  const existingSlides = slideCreationComponent.data.progressSlides || [];
  const totalSlides = slideCreationComponent.data.count || event.totalSlides || 1;

  const progressSlides = [...existingSlides];
  while (progressSlides.length < totalSlides) {
    const nextIndex = progressSlides.length;
    const ordinal = nextIndex + 1;
    progressSlides.push({
      completed: false,
      inProgress: false,
      slideIndex: nextIndex,
      slideNumber: ordinal,
      displayNumber: null,
      ordinal,
      totalCount: totalSlides,
      title: `Slide ${ordinal} of ${totalSlides}`,
      isPlaceholder: true
    });
  }

  if (event.slideIndex < progressSlides.length) {
    const existingSlot = progressSlides[event.slideIndex] || {};
    const fallbackDisplayNumber = Number.isFinite(event.displayNumber)
      ? event.displayNumber
      : (event.slideIndex + 1);
    progressSlides[event.slideIndex] = {
      ...existingSlot,
      completed: event.completed || false,
      inProgress: event.inProgress || false,
      slideIndex: event.slideIndex,
      slideNumber: existingSlot.slideNumber ?? (event.slideIndex + 1),
      displayNumber: existingSlot.displayNumber ?? fallbackDisplayNumber,
      isPlaceholder: existingSlot.isPlaceholder !== false
    };
  }

  const completedCount = progressSlides.filter(s => s.completed).length;

  const components = componentUpdaters.updateSlideCreationComponent(
    componentsState,
    `Creating slides... ${completedCount}/${totalSlides} completed`,
    totalSlides,
    true,
    null,
    slideCreationComponent.data.slideIds || [],
    slideCreationComponent.data.slideIndices || [],
    completedCount,
    progressSlides,
    slideCreationComponent.data.toolId  // Preserve existing toolId
  );

  return {
    ...state,
    components,
    metadata: {
      ...state.metadata,
      isCreatingSlides: true,
      totalSlidesToCreate: totalSlides,
      slideCreationStartTime: state.metadata?.slideCreationStartTime || Date.now(),
      slidesCompleted: completedCount,
      statusLabel: `Creating slides... ${completedCount}/${totalSlides}`
    }
  };
};

// =============================================================================
// SECTION 3: EVENT HANDLERS
// Original file: event-handlers.js (235 lines)
// Includes: timer, planning (plan_todos), research (citations, report)
// =============================================================================

// -----------------------------------------------------------------------------
// Timer Handler
// -----------------------------------------------------------------------------

export const buildTimerComponent = (state) => {
  if (!state.metadata?.startTime) {
    return null;
  }

  const isStreaming = state.metadata?.isStreaming ?? true;
  const parseTimestamp = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const startTimeMs = parseTimestamp(state.metadata.startTime);
  if (!Number.isFinite(startTimeMs)) {
    return null;
  }

  // IMPORTANT: Only use Date.now() for live streaming, never for completed states
  const resolvedEndMs =
    parseTimestamp(state.metadata.endTime) ??
    parseTimestamp(state.metadata.completedAt) ??
    null;  // Don't use Date.now() as fallback

  const computeElapsed = (start, end) => {
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return 0;
    }
    return Math.max(0, Math.floor((end - start) / 1000));
  };

  let elapsedSeconds;
  if (isStreaming) {
    // Live streaming - Date.now() is correct for live counter
    elapsedSeconds = Math.max(1, computeElapsed(startTimeMs, Date.now()));
  } else {
    // Not streaming - use recorded values only
    elapsedSeconds = state.metadata?.durationSeconds
      ?? (resolvedEndMs !== null ? computeElapsed(startTimeMs, resolvedEndMs) : 0);
  }

  // FIX: Include isComplete flag for reliable completion detection in UI
  const isComplete = state.metadata?.isComplete === true;
  
  return {
    type: 'timer',
    data: {
      startTime: state.metadata.startTime,
      elapsedSeconds,
      isStreaming,
      isComplete,
      completedAt: !isStreaming ? (state.metadata?.endTime || state.metadata?.completedAt || resolvedEndMs) : null,
      statusLabel: state.metadata?.statusLabel || null,
      statusHint: state.metadata?.statusHint || null
    },
    timestamp: new Date().toISOString()
  };
};

// -----------------------------------------------------------------------------
// Planning Handler (plan_todos)
// -----------------------------------------------------------------------------

// Pattern matching for generic/useless todos that should be filtered out
const GENERIC_TODO_PATTERNS = [
  /understand(?:ing)?\s+the\s+(?:user|client|request|brief)/i,
  /review\s+(?:the\s+)?prompt/i,
  /summari[sz]e\s+(?:the\s+)?request/i,
  /capture\s+(?:the\s+)?goals?/i
];

/**
 * Handle todoList events from Claude Agent SDK
 * SDK emits todoList events with task updates for UI display
 */
export const handlePlanTodosEvent = (event, state, _componentUpdaters, generateMessageId) => {
  const todos = Array.isArray(event.todos) ? event.todos : [];
  const normalizedTodos = mapRawTodos(todos);

  // Only filter out truly generic/useless todos, keep everything else
  const actionableTodos = normalizedTodos.filter(todo => {
    if (!todo || !todo.content) {
      return false;
    }
    // Only filter out obvious generic todos that don't provide value
    const isGeneric = typeof todo.content === 'string' && GENERIC_TODO_PATTERNS.some(pattern => pattern.test(todo.content));
    return !isGeneric; // Keep all non-generic todos
  });

  const components = Array.isArray(state.components)
    ? state.components.filter(component => component?.type !== 'todoList')
    : [];

  const previousPlanMeta = state.metadata?.plan?.todoMeta || {};
  const eventMeta = (event.meta && typeof event.meta === 'object') ? { ...event.meta } : null;
  const updatedAt = event.timestamp || new Date().toISOString();

  const nextTodoMeta = {
    ...previousPlanMeta,
    status: event.status || previousPlanMeta.status || 'update',
    updatedAt,
    source: event.source || eventMeta?.source || previousPlanMeta.source || null,
    version: event.version || eventMeta?.version || previousPlanMeta.version || null
  };

  if (eventMeta) {
    nextTodoMeta.origin = Object.prototype.hasOwnProperty.call(eventMeta, 'origin')
      ? eventMeta.origin
      : nextTodoMeta.origin;
    nextTodoMeta.bootstrap = Object.prototype.hasOwnProperty.call(eventMeta, 'bootstrap')
      ? eventMeta.bootstrap
      : nextTodoMeta.bootstrap;
    nextTodoMeta.replaces = Object.prototype.hasOwnProperty.call(eventMeta, 'replaces')
      ? eventMeta.replaces
      : null;
    nextTodoMeta.notes = Object.prototype.hasOwnProperty.call(eventMeta, 'notes')
      ? eventMeta.notes
      : nextTodoMeta.notes;
    nextTodoMeta.details = eventMeta;
  } else if (nextTodoMeta.details) {
    nextTodoMeta.details = { ...nextTodoMeta.details };
  }

  if (Object.prototype.hasOwnProperty.call(event, 'activeTodoId')) {
    nextTodoMeta.activeTodoId = event.activeTodoId;
  }

  return {
    ...state,
    components,
    metadata: {
      ...state.metadata,
      plan: {
        ...(state.metadata?.plan || {}),
        todo: actionableTodos,
        todoMeta: nextTodoMeta
      }
    },
    aiMessageId: state.aiMessageId || generateMessageId()
  };
};

// -----------------------------------------------------------------------------
// Research Handlers (citations, report)
// -----------------------------------------------------------------------------

export const handleCitationsEvent = (event, state, componentUpdaters) => {
  const citations = Array.isArray(event.citations) && event.citations.length > 0
    ? event.citations
    : (Array.isArray(event.sources) ? event.sources : []);

  if (citations.length === 0 && !Array.isArray(event.sourceSummaries)) {
    // Nothing new to apply yet – keep streaming status as-is.
    return state;
  }

  console.log('📚 Processing citations event');

  const duration = state.metadata?.researchStartTime
    ? `${Math.floor((Date.now() - state.metadata.researchStartTime) / 60000)}m ${Math.floor(((Date.now() - state.metadata.researchStartTime) % 60000) / 1000)}s`
    : null;

  let components = componentUpdaters.markThinkingComplete(state.components);

  components = componentUpdaters.updateCitationsComponent(
    components,
    event.researchQuery || 'Research',
    citations,
    false,
    duration,
    'Research ready',
    event.sourceSummaries
  );

  // CRITICAL FIX: Update metadata with citations and sourceSummaries
  // This ensures data persists when components are rebuilt from metadata after page reload
  const updatedMetadata = {
    ...state.metadata,
    action: 'research',
    researchQuery: event.researchQuery || state.metadata?.researchQuery || 'Research',
    citations,
    citationsDuration: duration || state.metadata?.citationsDuration,
    sourceSummaries: event.sourceSummaries || state.metadata?.sourceSummaries || [],
    searchQueries: state.metadata?.searchQueries || []  // Preserve search queries
  };

  return {
    ...state,
    components,
    metadata: updatedMetadata
  };
};

export const handleReportEvent = (event, state, componentUpdaters) => {
  // Support both old format (event.report) and new structured format (event.data)
  const reportData = event.data || {};
  const keyFindings = Array.isArray(reportData.keyFindings)
    ? reportData.keyFindings
    : (Array.isArray(event.keyFindings) ? event.keyFindings : []);
  const fullReport = reportData.fullReport || event.fullReport || event.report || '';
  const status = typeof event.status === 'string' ? event.status : null;
  const researchQuery = event.researchQuery
    || reportData.researchQuery
    || reportData.query
    || state.metadata?.researchQuery
    || 'Research';
  const sourceSummaries = Array.isArray(event.sourceSummaries)
    ? event.sourceSummaries
    : (Array.isArray(reportData.sourceSummaries) ? reportData.sourceSummaries : []);

  if (!fullReport && keyFindings.length === 0 && !status) {
    return state;
  }

  console.log('📋 Processing report event:', {
    hasStructuredData: !!event.data,
    keyFindingsCount: keyFindings.length,
    fullReportLength: fullReport.length,
    isComplete: event.isComplete
  });

  const duration = state.metadata?.researchStartTime
    ? `${Math.floor((Date.now() - state.metadata.researchStartTime) / 60000)}m ${Math.floor(((Date.now() - state.metadata.researchStartTime) % 60000) / 1000)}s`
    : null;

  let components = componentUpdaters.markThinkingComplete(state.components);

  components = componentUpdaters.updateReportComponent(
    components,
    researchQuery,
    keyFindings,  // NEW: Pass key findings array
    fullReport,   // Full report
    duration,
    status || (event.isComplete ? 'Research ready' : 'Compiling findings'),
    sourceSummaries,
    state.metadata?.searchQueries || []  // Pass search queries from metadata
  );

  // DIAGNOSTIC: Check if searchQueries exist right after updateReportComponent
  const researchComponent = components.find(c => c.type === 'research');
  console.log('🔍 [handleReportEvent] After updateReportComponent:', {
    hasComponent: !!researchComponent,
    dataKeys: Object.keys(researchComponent?.data || {}),
    hasSearchQueries: Array.isArray(researchComponent?.data?.searchQueries),
    searchQueriesCount: researchComponent?.data?.searchQueries?.length || 0
  });

  // CRITICAL FIX: Update metadata with keyFindings, fullReport, and sourceSummaries
  // This ensures data persists when components are rebuilt from metadata after page reload
  const updatedMetadata = {
    ...state.metadata,
    action: 'research',
    researchQuery,
    keyFindings: keyFindings.length > 0 ? keyFindings : state.metadata?.keyFindings,
    fullReport: fullReport || state.metadata?.fullReport,
    researchReport: fullReport || state.metadata?.researchReport,  // Backwards compat
    reportDuration: duration || state.metadata?.reportDuration,
    sourceSummaries: sourceSummaries.length > 0 ? sourceSummaries : (state.metadata?.sourceSummaries || []),
    citations: state.metadata?.citations || [],  // Preserve existing citations
    searchQueries: state.metadata?.searchQueries || []  // Preserve search queries
  };

  return {
    ...state,
    components,
    metadata: updatedMetadata
  };
};

// =============================================================================
// SECTION 4: COMPONENT UPDATERS
// Original file: componentUpdaters.js (980 lines)
// =============================================================================

/**
 * Collapse a run of consecutive complete thinking components at the tail of the
 * components array into one merged entry. When the model emits multiple thinking
 * blocks between tool calls, markThinkingComplete marks them all complete, and
 * this helper fuses them so the timeline shows a single "Thought for Ns" entry
 * with every block's content available in one dropdown.
 */
const mergeTrailingThinking = (components) => {
  if (!Array.isArray(components) || components.length < 2) return components;

  // Walk back from the tail while we see completed thinking blocks
  let runStart = components.length;
  for (let i = components.length - 1; i >= 0; i--) {
    const c = components[i];
    if (c?.type === 'thinking' && c.isComplete === true) {
      runStart = i;
      continue;
    }
    break;
  }

  const runLength = components.length - runStart;
  if (runLength < 2) return components;

  const run = components.slice(runStart);
  const mergedContent = run
    .map((b) => (typeof b?.content === 'string' ? b.content : ''))
    .filter((s) => s.length > 0)
    .join('\n\n');

  const startTimes = run.map((b) => b?.thinkingStartTime).filter(Number.isFinite);
  const endTimes = run.map((b) => b?.thinkingEndTime).filter(Number.isFinite);
  const durations = run.map((b) => b?.thinkingDurationSeconds).filter(Number.isFinite);
  const summedDuration = durations.reduce((sum, d) => sum + d, 0);

  const merged = {
    type: 'thinking',
    content: mergedContent,
    thinkingStartTime: startTimes.length ? Math.min(...startTimes) : undefined,
    thinkingEndTime: endTimes.length ? Math.max(...endTimes) : undefined,
    thinkingDurationSeconds: summedDuration,
    isLoading: false,
    isComplete: true,
    isCollapsed: true,
    timestamp: new Date().toISOString()
  };

  return [...components.slice(0, runStart), merged];
};

export const componentUpdaters = {
  updateThinkingComponent: (components, content) => {
    // Find existing incomplete thinking component
    const thinkingIndex = components.findIndex(c => c.type === 'thinking' && c.isComplete !== true);

    if (thinkingIndex >= 0) {
      const updated = [...components];
      const thinkingComponent = updated[thinkingIndex];
      const previousContent = thinkingComponent.content || '';
      let nextContent = content || '';

      if (previousContent && nextContent) {
        if (nextContent.startsWith(previousContent)) {
          nextContent = nextContent;
        } else if (previousContent.endsWith(nextContent)) {
          nextContent = previousContent;
        } else {
          nextContent = `${previousContent}${nextContent}`;
        }
      } else if (!nextContent) {
        nextContent = previousContent;
      }

      updated[thinkingIndex] = {
        ...thinkingComponent,
        content: nextContent,
        isLoading: true,
        isComplete: false,
        isCollapsed: false,  // Keep expanded while streaming
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    // Create new thinking component
    // This creates separate blocks when previous thinking was completed
    const now = Date.now();
    return [
      ...components,
      {
        type: 'thinking',
        content: content || '',
        thinkingStartTime: now,  // Track session start time
        isLoading: true,
        isComplete: false,
        isCollapsed: false,  // Start expanded while streaming
        timestamp: new Date().toISOString()
      }
    ];
  },

  updateTextComponent: (components, content, forceNew = false, options = {}) => {
    if (!content || typeof content !== 'string') {
      return components;
    }

    const { isFinal = false } = options || {};
    const trimmedNew = content.trim();
    if (trimmedNew.length === 0) {
      return components;
    }

    if (!forceNew && components.length > 0) {
      const lastComponent = components[components.length - 1];
      if (lastComponent.type === 'text') {
        if (isFinal && lastComponent.streamingDelta) {
          const { streamingDelta, ...rest } = lastComponent;
          return [
            ...components.slice(0, -1),
            {
              ...rest,
              content,
              timestamp: new Date().toISOString()
            }
          ];
        }

        const existingContent = lastComponent.content || '';
        const trimmedExisting = existingContent.trim();
        const existingIsBullet = trimmedExisting.startsWith('- ') || trimmedExisting.startsWith('* ');
        const newIsBullet = trimmedNew.startsWith('- ') || trimmedNew.startsWith('* ');

        if (!existingIsBullet && !newIsBullet) {
          return [
            ...components.slice(0, -1),
            {
              ...lastComponent,
              content: `${existingContent}\n${trimmedNew}`,
              timestamp: new Date().toISOString()
            }
          ];
        }
      }
    }

    return [
      ...components,
      {
        type: 'text',
        content,
        timestamp: new Date().toISOString()
      }
    ];
  },

  appendTextDelta: (components, delta) => {
    if (!delta || typeof delta !== 'string') {
      return components;
    }

    if (components.length === 0) {
      return [
        {
          type: 'text',
          content: delta,
          timestamp: new Date().toISOString(),
          streamingDelta: true
        }
      ];
    }

    const lastComponent = components[components.length - 1];
    // Only append to the trailing text component if it's still streaming
    // (`streamingDelta === true`). When a content_block_stop arrives we strip
    // that flag, which makes the next delta start a fresh component — that's
    // how multi-text-block assistant messages (common with Opus 4.7 adaptive
    // thinking) keep their text segments separate instead of merging.
    if (lastComponent.type === 'text' && lastComponent.streamingDelta === true) {
      const updated = [...components];
      updated[updated.length - 1] = {
        ...lastComponent,
        content: `${lastComponent.content || ''}${delta}`,
        timestamp: new Date().toISOString(),
        streamingDelta: true
      };
      return updated;
    }

    return [
      ...components,
      {
        type: 'text',
        content: delta,
        timestamp: new Date().toISOString(),
        streamingDelta: true
      }
    ];
  },

  /**
   * Record a page the agent fetched with WebFetch onto the research card.
   *
   * The inline `Fetching webpage ×N` bullet merges every fetch into a counter
   * and keeps no URL, so without this the user can see that 20 pages were read
   * but never which ones. Provenance is the point of the card in a finance
   * product, so the URL is stored on the research component: toolStatus is a
   * non-essential component type and gets dropped from the persisted
   * transcript under quota pressure, while `research` survives a reload.
   */
  addFetchedPage: (components, { url, toolId = null, timestamp = null } = {}) => {
    // Tool arguments are model-derived. A non-http(s) value is never recorded,
    // so nothing downstream can turn one into a link.
    const key = normalizeSourceUrl(url);
    if (!key) return components;

    const entry = {
      url: url.trim(),
      toolId,
      timestamp: timestamp || new Date().toISOString()
    };

    const existingIndex = components.findIndex(c => c.type === 'research');

    if (existingIndex >= 0) {
      const existingData = components[existingIndex].data || {};
      const existingPages = Array.isArray(existingData.fetchedPages) ? existingData.fetchedPages : [];
      if (existingPages.some(page => normalizeSourceUrl(page?.url) === key)) {
        return components;
      }

      const updated = [...components];
      updated[existingIndex] = {
        ...updated[existingIndex],
        data: {
          ...existingData,
          fetchedPages: [...existingPages, entry]
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    // A fetch with no prior search is still research: the agent read a page,
    // and the user is owed that source.
    return [...components, {
      type: 'research',
      data: {
        query: 'Research',
        keyFindings: [],
        report: '',
        fullReport: '',
        reportDuration: null,
        citations: [],
        citationsDuration: null,
        sourceSummaries: [],
        searchQueries: [],
        fetchedPages: [entry],
        isLoading: true,
        status: null
      },
      timestamp: new Date().toISOString()
    }];
  },

  updateCitationsComponent: (components, query, sources, isLoading, duration = null, status = null, sourceSummaries = null) => {
    const existingIndex = components.findIndex(c => c.type === 'research');

    if (existingIndex >= 0) {
      const updated = [...components];
      const existingData = updated[existingIndex].data;
      const existingCitations = Array.isArray(existingData.citations) ? existingData.citations : [];
      const hasNewCitations = Array.isArray(sources) && sources.length > 0;
      // MERGE incoming citations with what's already on the card. Multiple
      // WebSearches in a single turn each emit their own citations event;
      // overwriting would drop earlier sources. Dedupe by URL (case-insensitive).
      let nextCitations = existingCitations;
      if (hasNewCitations) {
        const seen = new Set(existingCitations.map(c => (c?.url || '').toLowerCase()));
        const merged = [...existingCitations];
        for (const c of sources) {
          const key = (c?.url || '').toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push(c);
        }
        nextCitations = merged;
      }

      const existingSummaries = Array.isArray(existingData.sourceSummaries) ? existingData.sourceSummaries : [];
      const summariesIsArray = Array.isArray(sourceSummaries);
      const hasNewSummaries = summariesIsArray && sourceSummaries.length > 0;
      const nextSummaries = hasNewSummaries
        ? sourceSummaries
        : (summariesIsArray && existingSummaries.length === 0 ? sourceSummaries : existingSummaries);

      updated[existingIndex] = {
        ...updated[existingIndex],
        data: {
          ...existingData,
          query: query || existingData.query || 'Research',
          citations: nextCitations,
          citationsDuration: duration || existingData.citationsDuration || null,
          sourceSummaries: nextSummaries,
          isLoading,
          status: status || existingData.status || (isLoading ? 'Researching…' : existingData.status || null)
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    return [...components, {
      type: 'research',
      data: {
        query: query || 'Research',
        report: null,
        reportDuration: null,
        citations: sources || [],
        citationsDuration: duration || null,
        sourceSummaries: Array.isArray(sourceSummaries) ? sourceSummaries : [],
        isLoading,
        status: status || (isLoading ? 'Researching…' : null)
      },
      timestamp: new Date().toISOString()
    }];
  },

  updateReportComponent: (components, query, keyFindings, fullReport, duration = null, status = null, sourceSummaries = null, searchQueries = []) => {
    const existingIndex = components.findIndex(c => c.type === 'research');

    if (existingIndex >= 0) {
      const updated = [...components];
      const existingData = updated[existingIndex].data;

      // Use new data if provided, otherwise keep existing
      const finalKeyFindings = Array.isArray(keyFindings) && keyFindings.length > 0
        ? keyFindings
        : (existingData.keyFindings || []);

      const finalFullReport = (typeof fullReport === 'string' && fullReport.trim())
        ? fullReport
        : (existingData.report || existingData.fullReport || '');

      const nextSummaries = Array.isArray(sourceSummaries) && sourceSummaries.length > 0
        ? sourceSummaries
        : (Array.isArray(existingData.sourceSummaries) ? existingData.sourceSummaries : []);

      const nextSearchQueries = Array.isArray(searchQueries) && searchQueries.length > 0
        ? searchQueries
        : (Array.isArray(existingData.searchQueries) ? existingData.searchQueries : []);

      updated[existingIndex] = {
        ...updated[existingIndex],
        data: {
          ...existingData,
          query: query || existingData.query || 'Research',
          keyFindings: finalKeyFindings,  // NEW: Structured key findings
          report: finalFullReport,         // Keep for backwards compat
          fullReport: finalFullReport,     // NEW: Full report field
          reportDuration: duration || existingData.reportDuration || null,
          citations: Array.isArray(existingData.citations) ? existingData.citations : [],
          citationsDuration: existingData.citationsDuration || null,
          sourceSummaries: nextSummaries,
          searchQueries: nextSearchQueries,
          isLoading: false,
          status: status || existingData.status || null
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    const newUnifiedComponent = {
      type: 'research',
      data: {
        query: query || 'Research',
        keyFindings: Array.isArray(keyFindings) ? keyFindings : [],  // NEW
        report: fullReport || '',         // Keep for backwards compat
        fullReport: fullReport || '',     // NEW
        reportDuration: duration,
        citations: [],
        citationsDuration: null,
        sourceSummaries: Array.isArray(sourceSummaries) ? sourceSummaries : [],
        searchQueries: Array.isArray(searchQueries) ? searchQueries : [],
        isLoading: false,
        status: status || null
      },
      timestamp: new Date().toISOString()
    };

    return [...components, newUnifiedComponent];
  },

  updateSlideCreationComponent: (components, status, count, isLoading = true, duration = null, slideIds = [], slideIndices = [], completedCount = 0, progressSlides = [], toolId = null) => {
    // Match by toolId if provided, otherwise find first
    const existingIndex = toolId
      ? components.findIndex(c => c.type === 'createSlides' && c.data?.toolId === toolId)
      : components.findIndex(c => c.type === 'createSlides');

    // FIX: Helper to ensure at least one slide has inProgress when loading
    const ensureInProgressSlide = (slides, loading) => {
      if (!loading || slides.length === 0) return slides;
      
      // Check if any slide is already in-progress
      const hasInProgress = slides.some(s => s.inProgress === true);
      if (hasInProgress) return slides;
      
      // Find first incomplete slide and mark as in-progress
      const firstIncompleteIndex = slides.findIndex(s => !s.completed);
      if (firstIncompleteIndex === -1) return slides;
      
      console.log('🔧 ensureInProgressSlide: Marking slide', firstIncompleteIndex, 'as in-progress');
      return slides.map((slide, idx) => ({
        ...slide,
        inProgress: idx === firstIncompleteIndex
      }));
    };

    if (existingIndex >= 0) {
      const updated = [...components];
      const previousData = updated[existingIndex].data;
      let nextProgressSlides = progressSlides.length > 0
        ? progressSlides
        : (previousData.progressSlides || []);
      
      // Ensure at least one slide has inProgress when loading
      nextProgressSlides = ensureInProgressSlide(nextProgressSlides, isLoading);
      
      const resolvedCompletedCount = progressSlides.length > 0
        ? nextProgressSlides.filter(slide => slide && !slide.isPlaceholder).length
        : Math.max(completedCount, previousData.completedCount || 0);
      const resolvedCount = count || previousData.count || nextProgressSlides.length;

      updated[existingIndex] = {
        ...updated[existingIndex],
        data: {
          ...previousData,
          toolId: toolId || previousData.toolId,
          status,
          count: resolvedCount,
          isLoading,
          duration: duration || previousData.duration,
          slideIds: slideIds.length > 0 ? slideIds : previousData.slideIds,
          slideIndices: slideIndices.length > 0 ? slideIndices : previousData.slideIndices,
          completedCount: resolvedCompletedCount,
          progressSlides: nextProgressSlides
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    // FIX: Also ensure inProgress for new component creation
    const ensuredProgressSlides = ensureInProgressSlide(progressSlides, isLoading);

    return [...components, {
      type: 'createSlides',
      data: {
        toolId,  // Store toolId for matching
        status,
        count,
        isLoading,
        duration,
        slideIds,
        slideIndices,
        completedCount,
        progressSlides: ensuredProgressSlides
      },
      timestamp: new Date().toISOString()
    }];
  },

  updateSlideEditComponent: (components, status, slideNumbers, isLoading = true, editedSlides = [], totalSlides = null, completedSlides = null, toolId = null) => {
    // Match by toolId if provided, otherwise find first
    const existingIndex = toolId
      ? components.findIndex(c => c.type === 'editSlides' && c.data?.toolId === toolId)
      : components.findIndex(c => c.type === 'editSlides');

    if (existingIndex >= 0) {
      const updated = [...components];
      const existingData = updated[existingIndex].data;

      // When editedSlides is provided, clear slideNumbers/slideIndices to ensure correct branch priority
      const shouldClearLegacyProps = editedSlides.length > 0;

      const buildChangeKey = (change) => {
        if (!change || typeof change !== 'object') {
          return null;
        }
        if (change.id != null) {
          return `id:${change.id}`;
        }
        if (change.nodeId != null) {
          return `node:${change.nodeId}`;
        }
        if (change.selector || change.target || change.type) {
          return [
            change.type ? `type:${change.type}` : '',
            change.selector ? `selector:${change.selector}` : '',
            change.target ? `target:${change.target}` : '',
            change.textBefore ? `before:${change.textBefore}` : '',
            change.textAfter ? `after:${change.textAfter}` : ''
          ].filter(Boolean).join('|');
        }
        try {
          return `json:${JSON.stringify(change)}`;
        } catch (serializationError) {
          console.warn('⚠️ Unable to serialize slide change for dedupe:', serializationError);
          return null;
        }
      };

      const mergeChanges = (existingChanges = [], incomingChanges = []) => {
        if (!Array.isArray(incomingChanges) || incomingChanges.length === 0) {
          return existingChanges;
        }
        const merged = Array.isArray(existingChanges) ? [...existingChanges] : [];
        const seen = new Set();
        merged.forEach((change) => {
          const key = buildChangeKey(change);
          if (key) {
            seen.add(key);
          }
        });
        incomingChanges.forEach((change) => {
          if (!change) {
            return;
          }
          const key = buildChangeKey(change);
          if (key && seen.has(key)) {
            return;
          }
          if (key) {
            seen.add(key);
          }
          merged.push(change);
        });
        return merged;
      };

      // ✅ FIX: Use Map-based deduplication to ensure uniqueness from the start
      const slidesMap = new Map();

      // First, add all existing slides to map (this normalizes any pre-existing duplicates)
      (existingData.editedSlides || []).forEach(slide => {
        if (slide?.slideId) {
          slidesMap.set(slide.slideId, {
            ...slide,
            changes: Array.isArray(slide.changes) ? slide.changes : []
          });
        }
      });

      // Then merge new slides, updating or adding as needed
      editedSlides.forEach(newSlide => {
        if (!newSlide?.slideId) return; // Skip invalid entries

        const existing = slidesMap.get(newSlide.slideId);
        if (existing) {
          // Update existing entry - dedupe changes array to prevent balloon
          slidesMap.set(newSlide.slideId, {
            ...existing,
            ...newSlide,
            changes: mergeChanges(existing.changes, newSlide.changes)
          });
        } else {
          // New slide - add to map with normalized changes array
          slidesMap.set(newSlide.slideId, {
            ...newSlide,
            changes: Array.isArray(newSlide.changes) ? mergeChanges([], newSlide.changes) : []
          });
        }
      });

      // Convert map back to array
      const mergedSlides = Array.from(slidesMap.values());

      updated[existingIndex] = {
        ...updated[existingIndex],
        data: {
          ...existingData,
          toolId: toolId || existingData.toolId,  // Preserve toolId
          status,
          isLoading,
          editedSlides: mergedSlides,  // ✅ Guaranteed unique by slideId
          totalSlides: totalSlides !== null ? totalSlides : existingData.totalSlides,
          completedSlides: completedSlides ?? mergedSlides.length,  // ✅ Now accurate
          // Clear these to ensure SlideEditCard takes the editedSlides branch
          slideNumbers: shouldClearLegacyProps ? null : existingData.slideNumbers,
          slideIndices: shouldClearLegacyProps ? null : existingData.slideIndices
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    return [...components, {
      type: 'editSlides',
      data: {
        toolId,  // Store toolId for matching
        status,
        slideNumbers,
        totalSlides: totalSlides ?? (slideNumbers ? slideNumbers.length : editedSlides.length),
        completedSlides: completedSlides ?? editedSlides.length,
        isLoading,
        editedSlides
      },
      timestamp: new Date().toISOString()
    }];
  },

  addPlanDecisionComponent: (components, planContent, prompt, showButtons = true, extras = {}) => {
    const decisionMeta = {
      type: 'planApproval',
      prompt: prompt || 'Would you like me to implement this plan and create the slides?',
      planContent,
      fullPlanContent: extras.fullPlanContent || planContent,
      status: extras.status || null,
      actions: Array.isArray(extras.planActions) ? extras.planActions : [],
      sections: Array.isArray(extras.planSections) ? extras.planSections : [],
      showButtons,
      blueprint: extras.planBlueprint || extras.fullPlanContent || null,
      updatedAt: new Date().toISOString()
    };

    return [...components, {
      type: 'planDecision',
      planContent,
      prompt: prompt || 'Would you like me to implement this plan and create the slides?',
      showButtons,
      planActions: Array.isArray(extras.planActions) ? extras.planActions : [],
      planSections: Array.isArray(extras.planSections) ? extras.planSections : [],
      fullPlanContent: extras.fullPlanContent || planContent,
      blueprint: extras.planBlueprint || extras.fullPlanContent || null,
      isStreaming: extras.isStreaming ?? !showButtons,
      timestamp: decisionMeta.updatedAt,
      decisionType: 'planApproval',
      decision: decisionMeta
    }];
  },

  // upsertUnifiedPlanComponent removed along with the rest of plan mode.

  upsertPlanTodoComponent: (components) => {
    if (!Array.isArray(components) || components.length === 0) {
      return components;
    }

    const filtered = components.filter(component => component.type !== 'todoList');
    return filtered.length === components.length ? components : filtered;
  },

  addRateLimitComponent: (components, rateLimitInfo) => {
    return [...components, {
      type: 'rateLimit',
      data: rateLimitInfo,
      timestamp: new Date().toISOString()
    }];
  },

  // One card per turn. A turn can emit several turn_error stages as it unwinds
  // and stacking them would bury the first, which is the one that says why.
  addTurnErrorComponent: (components, turnErrorInfo) => {
    if (components.some(component => component.type === 'turnError')) {
      return components;
    }
    return [...components, {
      type: 'turnError',
      data: turnErrorInfo,
      timestamp: new Date().toISOString()
    }];
  },

  // addClarificationComponent removed: clarification events are gone.

  updateLoopTimelineComponent: (components, payload = {}) => {
    if (!payload || !Array.isArray(payload.steps)) {
      return components;
    }

    const loopComponent = {
      type: 'loopTimeline',
      data: payload,
      timestamp: new Date().toISOString()
    };

    const existingIndex = components.findIndex(component => component.type === 'loopTimeline');
    if (existingIndex >= 0) {
      const updated = [...components];
      updated[existingIndex] = loopComponent;
      return updated;
    }

    const thinkingIndex = components.findIndex(component => component.type === 'thinking');
    if (thinkingIndex >= 0) {
      return [
        ...components.slice(0, thinkingIndex + 1),
        loopComponent,
        ...components.slice(thinkingIndex + 1)
      ];
    }

    return [loopComponent, ...components];
  },

  markThinkingComplete: (components) => {
    const marked = components.map(component => {
      if (component.type === 'thinking' && !component.isComplete) {
        const endTime = Date.now();
        const startTime = component.thinkingStartTime || endTime;
        const durationSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));

        return {
          ...component,
          thinkingEndTime: endTime,  // Record when this thinking session ended
          thinkingDurationSeconds: durationSeconds,  // Per-block duration
          isLoading: false,
          isComplete: true,
          isCollapsed: true  // Auto-collapse when thinking session completes
        };
      }
      return component;
    });
    // Collapse consecutive complete thinking blocks at the tail into one entry.
    return mergeTrailingThinking(marked);
  },

  markAllComponentsComplete: (components) => {
    const marked = components.map(component => {
      // CRITICAL FIX: Handle thinking components FIRST to ensure proper completion with duration
      // This prevents the generic 'isLoading' handler from processing thinking blocks incorrectly
      if (component.type === 'thinking') {
        // If already complete, preserve existing state
        if (component.isComplete) {
          return component;
        }
        // Otherwise, mark as complete with duration calculation
        const endTime = Date.now();
        const startTime = component.thinkingStartTime || endTime;
        const durationSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));
        return {
          ...component,
          thinkingEndTime: endTime,
          thinkingDurationSeconds: durationSeconds,
          isLoading: false,
          isComplete: true,
          isCollapsed: true
        };
      }

      if (component.type === 'timer') {
        const completedAt = new Date().toISOString();
        return {
          ...component,
          data: {
            ...(component.data || {}),
            isStreaming: false,
            completedAt,
            elapsedSeconds: Math.max(0, component.data?.elapsedSeconds ?? 0)
          },
          timestamp: completedAt
        };
      }

      if (component.data && 'isLoading' in component.data) {
        return {
          ...component,
          data: {
            ...component.data,
            isLoading: false,
            isComplete: component.data?.isComplete !== undefined
              ? component.data.isComplete
              : (component.type === 'createSlides' || component.type === 'editSlides' || component.type === 'research' || component.type === 'slideWork')
        }
      };
      }

      if ('isLoading' in component) {
        return {
          ...component,
          isLoading: false,
          isComplete: true
        };
      }

      switch (component.type) {
        case 'createSlides':
          return {
            ...component,
            data: {
              ...component.data,
              isLoading: false,
              isComplete: true,
              status: component.data.status?.includes('✅')
                ? component.data.status
                : `Cancelled - ${component.data.completedCount || 0}/${component.data.count} completed`
            }
          };
        case 'editSlides':
          return {
            ...component,
            data: {
              ...component.data,
              isLoading: false,
              isComplete: true,
              status: component.data.status?.includes('✅')
                ? component.data.status
                : `Cancelled - ${component.data.completedSlides || 0}/${component.data.totalSlides || 0} updated`
            }
          };
        case 'citations':
          return {
            ...component,
            data: {
              ...component.data,
              isLoading: false
            }
          };
        case 'research':
          return {
            ...component,
            data: {
              ...component.data,
              isLoading: false,
              isComplete: true,
              status: component.data.status || 'Research complete'
            }
          };
        case 'planDecision':
          return {
            ...component,
            isStreaming: false
          };
        default:
          return component;
      }
    });
    // End-of-turn: collapse any remaining consecutive thinking tail into one entry.
    return mergeTrailingThinking(marked);
  },

  /**
   * Upserts telemetry task into metadata.plan.todo array
   * Merges SDK hook events (tool_start, tool_complete) with existing todos by toolCallId
   *
   * @param {Object} state - Current stream state
   * @param {Object} telemetryEvent - SDK telemetry event with toolId, event type, timestamp
   * @returns {Object} Updated state with merged todo entry
   */
  upsertTelemetryTask: (state, telemetryEvent) => {
    const { event, tool, toolId, timestamp, success } = telemetryEvent;

    if (!toolId) {
      // No toolId means we can't merge - skip
      return state;
    }

    const existingPlanMeta = state.metadata?.plan || {};
    const existingTodos = Array.isArray(existingPlanMeta.todo) ? existingPlanMeta.todo : [];

    // Find existing todo by toolCallId
    const existingIndex = existingTodos.findIndex(todo =>
      todo.metadata?.toolCallId === toolId || todo.toolCallId === toolId
    );

    let updatedTodos;
    const now = timestamp || Date.now();

    if (event === 'tool_start') {
      if (existingIndex >= 0) {
        // Update existing entry
        updatedTodos = [...existingTodos];
        updatedTodos[existingIndex] = {
          ...updatedTodos[existingIndex],
          status: 'in_progress',
          startedAt: now,
          metadata: {
            ...updatedTodos[existingIndex].metadata,
            toolCallId: toolId,
            source: 'sdk_telemetry'
          }
        };
      } else {
        // Create new entry
        updatedTodos = [
          ...existingTodos,
          {
            id: toolId,
            toolCallId: toolId,
            tool,
            content: MCP_TOOL_LABELS[tool] || `Running ${tool}`,
            status: 'in_progress',
            startedAt: now,
            metadata: {
              toolCallId: toolId,
              toolName: tool,
              source: 'sdk_telemetry'
            }
          }
        ];
      }
    } else if (event === 'tool_heartbeat') {
      // Heartbeat: Keep entry as in_progress and update lastHeartbeat timestamp
      // This prevents timeout for long-running tools
      if (existingIndex >= 0) {
        updatedTodos = [...existingTodos];
        updatedTodos[existingIndex] = {
          ...updatedTodos[existingIndex],
          status: 'in_progress',
          lastHeartbeat: now,
          metadata: {
            ...updatedTodos[existingIndex].metadata,
            toolCallId: toolId,
            source: 'sdk_telemetry',
            lastHeartbeat: now
          }
        };
      } else {
        // Heartbeat arrived but no entry exists - this shouldn't happen
        // Heartbeats should only come after tool_start
        // Ignore and return unchanged state
        console.warn('⚠️ Received tool_heartbeat without prior tool_start:', { toolId, tool });
        return state;
      }
    } else if (event === 'tool_complete') {
      if (existingIndex >= 0) {
        // Update existing entry to completed
        updatedTodos = [...existingTodos];
        updatedTodos[existingIndex] = {
          ...updatedTodos[existingIndex],
          status: success ? 'completed' : 'failed',
          completedAt: now,
          metadata: {
            ...updatedTodos[existingIndex].metadata,
            toolCallId: toolId,
            source: 'sdk_telemetry',
            success
          }
        };
      } else {
        // Tool completed but we never saw tool_start - just mark as completed
        updatedTodos = [
          ...existingTodos,
          {
            id: toolId,
            toolCallId: toolId,
            tool,
            content: MCP_TOOL_LABELS[tool] ? `${MCP_TOOL_LABELS[tool]} complete` : `Completed ${tool}`,
            status: success ? 'completed' : 'failed',
            completedAt: now,
            metadata: {
              toolCallId: toolId,
              toolName: tool,
              source: 'sdk_telemetry',
              success
            }
          }
        ];
      }
    } else {
      // Unknown event type
      return state;
    }

    return {
      ...state,
      metadata: {
        ...state.metadata,
        plan: {
          ...existingPlanMeta,
          todo: updatedTodos,
          todoMeta: {
            ...(existingPlanMeta.todoMeta || {}),
            updatedAt: new Date().toISOString(),
            source: 'sdk_telemetry_merged'
          }
        }
      }
    };
  },

  /**
   * Mark a card component as complete when its corresponding tool finishes.
   * Currently handles: web_search → research card (sets isLoading: false +
   * marks the matching per-bar searchQuery completed so its shimmer stops).
   * Slide cards complete via markAllComponentsComplete at turn end.
   */
  completeToolCard: (state, toolName, toolId = null) => {
    if (toolName === 'web_search' || toolName === 'WebSearch') {
      const components = [...state.components];
      const idx = components.findIndex(c => c.type === 'research');
      if (idx >= 0) {
        const data = components[idx].data || {};
        const existingQueries = Array.isArray(data.searchQueries) ? data.searchQueries : [];
        // Mark the bar matching this toolId completed. If no toolId is
        // provided, fall back to marking the last still-active bar completed
        // — the SDK's web_search platform tool sometimes emits tool_complete
        // without the originating callId, so we need a deterministic fallback.
        let updatedQueries = existingQueries;
        if (existingQueries.length > 0) {
          // First try matching by toolId. If that fails (e.g. toolId routing
          // mismatch between PreToolUse search_query and PostToolUse
          // tool_complete), fall through to "mark the last in-flight bar".
          // Without this fallback a misrouted tool_complete leaves the bar
          // shimmering forever even though the search has finished.
          let matchIdx = toolId
            ? existingQueries.findIndex(q => q.toolId === toolId)
            : -1;
          if (matchIdx < 0) {
            matchIdx = existingQueries
              .map((q, i) => ({ q, i }))
              .filter(({ q }) => !q.completed)
              .map(({ i }) => i)
              .pop() ?? -1;
          }
          if (matchIdx >= 0) {
            updatedQueries = existingQueries.map((q, i) =>
              i === matchIdx ? { ...q, completed: true } : q
            );
          }
        }
        components[idx] = {
          ...components[idx],
          data: { ...data, isLoading: false, status: null, searchQueries: updatedQueries },
          timestamp: new Date().toISOString()
        };
        // Keep metadata.searchQueries in sync so future search_query events
        // see the same completion state when they preserve previous entries.
        const metaQueries = Array.isArray(state.metadata?.searchQueries) ? state.metadata.searchQueries : [];
        const updatedMetaQueries = metaQueries.map(q =>
          (toolId && q.toolId === toolId) || (!toolId && !q.completed)
            ? { ...q, completed: true }
            : q
        );
        return {
          ...state,
          components,
          metadata: { ...state.metadata, searchQueries: updatedMetaQueries }
          // Note: we intentionally do NOT toggle any research-capture flag here.
          // Text routing is gated exclusively by metadata.activeResearchAgentId,
          // which is set/cleared by subagent_start/subagent_complete telemetry.
        };
      }
    }
    // execute_office_js no longer mutates a per-tool card here — it aggregates
    // into the unified slideWork card built up turn-wide via addSlideWorkUpdate.
    // slideWork's per-tool lifecycle is handled in the addSlideWorkUpdate
    // updater itself (operationCount increments, slide indices accumulate),
    // and finalization happens in markAllComponentsComplete.
    return state;
  },

  /** Add or update the single connected-app work group for a target app. */
  upsertConnectedAppWorkComponent: (components, {
    app,
    toolId,
    toolName,
    label,
    timestamp = null
  } = {}) => {
    if (!CONNECTED_APPS.has(app) || !toolId) return components;

    const componentIndex = components.findIndex(component =>
      component.type === 'connectedAppWork' && component.data?.app === app
    );
    const existing = componentIndex >= 0 ? components[componentIndex] : null;
    const existingSteps = Array.isArray(existing?.data?.steps) ? existing.data.steps : [];
    const existingStepIndex = existingSteps.findIndex(step => step.toolId === toolId);
    const nextStep = {
      ...(existingStepIndex >= 0 ? existingSteps[existingStepIndex] : {}),
      toolId,
      toolName,
      label: label || humanizeToolName(toolName),
      status: 'in_progress',
      error: null,
      startedAt: timestamp || new Date().toISOString(),
      completedAt: null
    };
    const steps = existingStepIndex >= 0
      ? existingSteps.map((step, index) => index === existingStepIndex ? nextStep : step)
      : [...existingSteps, nextStep];
    const nextComponent = {
      type: 'connectedAppWork',
      data: {
        ...(existing?.data || {}),
        app,
        steps,
        status: 'in_progress',
        isLoading: true,
        isComplete: false,
        hasError: steps.some(step => step.status === 'failed')
      },
      timestamp: timestamp || new Date().toISOString()
    };

    if (componentIndex < 0) {
      return [...components, nextComponent];
    }

    const updated = [...components];
    updated[componentIndex] = nextComponent;
    return updated;
  },

  /** Complete one child step and derive the group state from all known steps. */
  completeConnectedAppWorkComponent: (components, toolId, success = true, error = null) => {
    if (!toolId) return components;
    const componentIndex = components.findIndex(component =>
      component.type === 'connectedAppWork' &&
      Array.isArray(component.data?.steps) &&
      component.data.steps.some(step => step.toolId === toolId)
    );
    if (componentIndex < 0) return components;

    const current = components[componentIndex];
    const completedAt = new Date().toISOString();
    const safeError = typeof error === 'string' ? error.trim().slice(0, 240) : null;
    const steps = current.data.steps.map(step => step.toolId === toolId
      ? {
          ...step,
          status: success ? 'completed' : 'failed',
          error: success ? null : (safeError || 'Could not complete this action'),
          completedAt
        }
      : step
    );
    const allResolved = steps.every(step => step.status === 'completed' || step.status === 'failed');
    const hasError = steps.some(step => step.status === 'failed');
    const updated = [...components];
    updated[componentIndex] = {
      ...current,
      data: {
        ...current.data,
        steps,
        status: allResolved ? (hasError ? 'failed' : 'completed') : 'in_progress',
        isLoading: !allResolved,
        isComplete: allResolved,
        hasError
      },
      timestamp: completedAt
    };
    return updated;
  },

  /** Settle any connected-app steps still open when their parent turn ends. */
  settleConnectedAppWorkComponents: (components, success = true) => {
    const completedAt = new Date().toISOString();
    return components.map(component => {
      if (component.type !== 'connectedAppWork') return component;

      const currentSteps = Array.isArray(component.data?.steps) ? component.data.steps : [];
      const steps = currentSteps.map(step => {
        if (step.status === 'completed' || step.status === 'failed') return step;
        return {
          ...step,
          status: success ? 'completed' : 'failed',
          error: success ? null : (step.error || 'Stopped before this action completed'),
          completedAt
        };
      });
      const hasError = steps.some(step => step.status === 'failed');
      return {
        ...component,
        data: {
          ...component.data,
          steps,
          status: hasError ? 'failed' : 'completed',
          isLoading: false,
          isComplete: true,
          hasError
        },
        timestamp: completedAt
      };
    });
  },

  /** Complete generic components and every connected-app child consistently. */
  settleAllComponents: (components, success = true) => {
    const completed = componentUpdaters.markAllComponentsComplete(components || []);
    return componentUpdaters.settleConnectedAppWorkComponents(completed, success);
  },

  /**
   * Add an inline tool status component. Used for tools that don't have a
   * dedicated card (read ops, screenshots, verification, etc.).
   *
   * Consecutive calls for the same tool (e.g. list_slide_shapes ×5) are merged
   * into the tail toolStatus component — the data.toolIds array collects every
   * underlying tool-call ID, and data.count tracks the total. Each ID still
   * completes independently via completeToolStatusComponent.
   */
  addToolStatusComponent: (components, toolId, toolName, label) => {
    if (!toolId) return components;

    // Skip if this exact toolId is already tracked anywhere (legacy single or merged).
    if (components.some(c =>
      c.type === 'toolStatus' && (
        c.data?.toolId === toolId ||
        (Array.isArray(c.data?.toolIds) && c.data.toolIds.includes(toolId))
      )
    )) {
      return components;
    }

    // If the tail is a toolStatus for the same tool, merge into it.
    const lastIdx = components.length - 1;
    const last = lastIdx >= 0 ? components[lastIdx] : null;
    if (last && last.type === 'toolStatus' && last.data?.toolName === toolName) {
      const existingIds = Array.isArray(last.data.toolIds) && last.data.toolIds.length > 0
        ? last.data.toolIds
        : (last.data.toolId ? [last.data.toolId] : []);
      const existingCompleted = Array.isArray(last.data.completedToolIds)
        ? last.data.completedToolIds
        : [];
      const nextToolIds = [...existingIds, toolId];
      const updated = [...components];
      updated[lastIdx] = {
        ...last,
        data: {
          ...last.data,
          toolId: existingIds[0] || toolId,
          toolIds: nextToolIds,
          completedToolIds: existingCompleted,
          count: nextToolIds.length,
          status: 'in_progress',
          label: last.data.label || label
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    // Otherwise append a fresh toolStatus.
    return [
      ...components,
      {
        type: 'toolStatus',
        data: {
          toolId,
          toolIds: [toolId],
          completedToolIds: [],
          count: 1,
          toolName,
          label,
          status: 'in_progress'
        },
        timestamp: new Date().toISOString()
      }
    ];
  },

  /**
   * Mark an inline tool status component as completed or failed. Handles both
   * the legacy single-toolId shape and the merged multi-id shape by looking up
   * via data.toolId OR data.toolIds. A merged card stays in_progress until
   * every constituent toolId has resolved.
   */
  completeToolStatusComponent: (components, toolId, success = true) => {
    if (!toolId) return components;
    const idx = components.findIndex(c =>
      c.type === 'toolStatus' && (
        c.data?.toolId === toolId ||
        (Array.isArray(c.data?.toolIds) && c.data.toolIds.includes(toolId))
      ) && c.data?.status !== 'completed' && c.data?.status !== 'failed'
    );
    if (idx < 0) return components;

    const current = components[idx];
    const data = current.data || {};
    const toolIds = Array.isArray(data.toolIds) && data.toolIds.length > 0
      ? data.toolIds
      : (data.toolId ? [data.toolId] : []);
    const completedIds = Array.isArray(data.completedToolIds) ? data.completedToolIds : [];
    const nextCompleted = completedIds.includes(toolId)
      ? completedIds
      : [...completedIds, toolId];
    const failedCount = (data.failedCount || 0) + (success ? 0 : 1);
    const allResolved = toolIds.length > 0 && toolIds.every(id => nextCompleted.includes(id));
    const nextStatus = allResolved
      ? (failedCount > 0 ? 'failed' : 'completed')
      : 'in_progress';

    const updated = [...components];
    updated[idx] = {
      ...current,
      data: {
        ...data,
        completedToolIds: nextCompleted,
        failedCount,
        status: nextStatus
      },
      timestamp: new Date().toISOString()
    };
    return updated;
  },

  /**
   * Aggregate every slide-modifying tool call within a turn into a single
   * `slideWork` card. The card grows as more distinct slide indices are
   * touched (extracted from OOXML tool args). For execute_office_js writes
   * we can't know which slide the AI's code targets, so the index stays
   * unknown and the card just renders "Working on slide" until a tool with
   * an extractable index fires.
   *
   * Lifecycle: one card per turn. The reverse-scan stops at the first
   * non-loading slideWork card so we never merge into a stale card from
   * a previous turn that somehow failed to finalize.
   */
  addSlideWorkUpdate: (components, { slideIndex = null, label = null, app = null } = {}) => {
    // Reverse-scan only the tail for an in-flight slideWork card.
    let mergeIdx = -1;
    for (let i = components.length - 1; i >= 0; i--) {
      const c = components[i];
      if (c.type === 'slideWork' && c.data?.isLoading === true) {
        mergeIdx = i;
        break;
      }
      // Stop scanning if we hit any other component type — the slideWork
      // card we want lives at or near the tail of the current turn.
      if (c.type === 'slideWork') break;
    }

    if (mergeIdx >= 0) {
      const existing = components[mergeIdx];
      const existingIndices = Array.isArray(existing.data.touchedSlideIndices)
        ? existing.data.touchedSlideIndices
        : [];
      const merged = new Set(existingIndices);
      if (Number.isFinite(slideIndex)) merged.add(slideIndex);
      const updated = [...components];
      updated[mergeIdx] = {
        ...existing,
        data: {
          ...existing.data,
          touchedSlideIndices: Array.from(merged).sort((a, b) => a - b),
          operationCount: (existing.data.operationCount || 0) + 1,
          latestLabel: label || existing.data.latestLabel || 'Working on slide',
          app: existing.data.app || app || null
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }

    return [
      ...components,
      {
        type: 'slideWork',
        data: {
          touchedSlideIndices: Number.isFinite(slideIndex) ? [slideIndex] : [],
          operationCount: 1,
          latestLabel: label || 'Working on slide',
          // Which Office host produced this card. Stored on the card rather
          // than read at render time because the message is persisted and
          // replicated into a shared cross-app chat: the wording has to follow
          // the app that did the work, not whichever pane is looking at it.
          // Null (the web shell, and every card written before this) renders
          // exactly as it did before, in PowerPoint's wording.
          app: app || null,
          isLoading: true,
          isComplete: false,
          startTime: Date.now()
        },
        timestamp: new Date().toISOString()
      }
    ];
  },

  /**
   * Append text delta to the most recent completed research card's fullReport.
   * Returns updated components array, or null if no eligible card found.
   */
  appendTextToResearchReport: (components, delta) => {
    if (!delta || typeof delta !== 'string') return null;
    // Once report text starts streaming, every in-flight search bar is by
    // definition done — the agent has the search results and is writing.
    // Mark each searchQueries entry completed so per-bar shimmer stops.
    const completeAllQueries = (queries) => Array.isArray(queries)
      ? queries.map((q) => (q?.completed ? q : { ...q, completed: true }))
      : queries;

    // Prefer a completed research card, but fall back to the most recent research card.
    let fallbackIndex = -1;
    for (let i = components.length - 1; i >= 0; i--) {
      const c = components[i];
      if (c.type !== 'research') {
        continue;
      }
      if (fallbackIndex === -1) {
        fallbackIndex = i;
      }
      if (!c.data?.isLoading) {
        const updated = [...components];
        updated[i] = {
          ...c,
          data: {
            ...c.data,
            fullReport: (c.data.fullReport || '') + delta,
            report: (c.data.report || '') + delta,
            isLoading: false,
            searchQueries: completeAllQueries(c.data.searchQueries),
            _streamingReport: true
          },
          timestamp: new Date().toISOString()
        };
        return updated;
      }
    }
    if (fallbackIndex >= 0) {
      const updated = [...components];
      const c = updated[fallbackIndex];
      updated[fallbackIndex] = {
        ...c,
        data: {
          ...c.data,
          fullReport: (c.data.fullReport || '') + delta,
          report: (c.data.report || '') + delta,
          isLoading: false,
          searchQueries: completeAllQueries(c.data.searchQueries),
          _streamingReport: true
        },
        timestamp: new Date().toISOString()
      };
      return updated;
    }
    return null; // No eligible card
  },

  /**
   * Finalize research report: clear the streaming flag, drop the card-level
   * loading state, and mark every search-query bar as completed. This is the
   * authoritative "research is done" sweep — runs on subagent_complete /
   * subagent_error and at turn end. Without it, a sub-agent that finishes
   * without writing a report leaves the last search bar shimmering forever.
   */
  finalizeResearchReport: (components) => {
    return components.map(c => {
      if (c.type !== 'research') return c;
      const { _streamingReport, ...rest } = c.data || {};
      const queries = Array.isArray(rest.searchQueries) ? rest.searchQueries : [];
      const finalizedQueries = queries.map(q => (q.completed ? q : { ...q, completed: true }));
      return {
        ...c,
        data: {
          ...rest,
          isLoading: false,
          searchQueries: finalizedQueries
        }
      };
    });
  },

  /**
   * Add subagent activity component
   * Creates a visual indicator for subagent work (slide-builder, research-analyst)
   *
   * @param {Array} components - Current components array
   * @param {Object} subagentInfo - Subagent metadata {agentId, agentLabel, description, status, timestamp}
   * @returns {Array} Updated components with subagent component
   */
  addSubagentComponent: (components, subagentInfo) => {
    const {
      agentId,
      agentLabel,
      description,
      status = 'working',
      timestamp,
      toolId  // CRITICAL FIX (Task 3): Add toolId for deterministic matching
    } = subagentInfo;

    const subagentComponent = {
      type: 'subagent',
      data: {
        agentId,
        agentLabel,
        description,
        status,
        toolId,  // CRITICAL FIX (Task 3): Store toolId for deterministic matching
        startedAt: timestamp || new Date().toISOString()
      },
      timestamp: timestamp || new Date().toISOString()
    };

    // Insert after thinking component if exists, otherwise at start
    const thinkingIndex = components.findIndex(c => c.type === 'thinking');
    if (thinkingIndex >= 0) {
      return [
        ...components.slice(0, thinkingIndex + 1),
        subagentComponent,
        ...components.slice(thinkingIndex + 1)
      ];
    }

    return [subagentComponent, ...components];
  },

  /**
   * Update existing subagent component status
   * Matches by toolId to update the correct subagent activity
   *
   * @param {Array} components - Current components array
   * @param {string} toolId - Tool use ID to match
   * @param {Object} updates - Updates to apply {status, duration, resultLength, error, timestamp}
   * @returns {Array} Updated components
   */
  updateSubagentComponent: (components, toolId, updates) => {
    // CRITICAL FIX (Task 3): Match by toolId instead of first working card
    // This prevents race conditions when multiple subagents run sequentially
    const subagentIndex = components.findIndex(c =>
      c.type === 'subagent' &&
      c.data?.toolId === toolId
    );

    if (subagentIndex < 0) {
      // No subagent with matching toolId found
      console.warn(`⚠️ No subagent found with toolId: ${toolId}`);
      return components;
    }

    const updated = [...components];
    const existing = updated[subagentIndex];

    updated[subagentIndex] = {
      ...existing,
      data: {
        ...existing.data,
        status: updates.status || existing.data.status,
        completedAt: updates.timestamp || new Date().toISOString(),
        duration: updates.duration,
        resultLength: updates.resultLength,
        error: updates.error
      },
      timestamp: updates.timestamp || new Date().toISOString()
    };

    return updated;
  },

  /**
   * Update task heartbeat for long-running tools
   * Matches by tool name (not toolCallId) since SDK doesn't pass toolCallId to tool handlers
   *
   * @param {Object} state - Current stream state
   * @param {string} toolName - Tool name to match (e.g., 'research', 'createSlides')
   * @param {string} timestamp - ISO timestamp of heartbeat
   * @returns {Object} Updated state with refreshed heartbeat timestamp
   */
  updateTaskHeartbeat: (state, toolName, timestamp) => {
    if (!toolName) {
      console.warn('⚠️ updateTaskHeartbeat called without toolName');
      return state;
    }

    const existingPlanMeta = state.metadata?.plan || {};
    const existingTodos = Array.isArray(existingPlanMeta.todo) ? existingPlanMeta.todo : [];

    // Find in_progress todo entries that match the tool name
    // Match by metadata.toolName OR tool field
    const matchingIndices = existingTodos
      .map((todo, index) => {
        const todoToolName = todo.metadata?.toolName || todo.tool;
        const isInProgress = todo.status === 'in_progress';
        const matches = todoToolName === toolName;
        return (isInProgress && matches) ? index : -1;
      })
      .filter(index => index >= 0);

    if (matchingIndices.length === 0) {
      console.log('⚠️ No in_progress todo found for heartbeat:', { toolName });
      return state;
    }

    // Update all matching entries (typically just one)
    const updatedTodos = [...existingTodos];
    const now = timestamp || new Date().toISOString();

    matchingIndices.forEach(index => {
      updatedTodos[index] = {
        ...updatedTodos[index],
        lastHeartbeat: now,
        metadata: {
          ...updatedTodos[index].metadata,
          lastHeartbeat: now
        }
      };
    });

    console.log('💓 Updated heartbeat for tool:', {
      toolName,
      matchingCount: matchingIndices.length,
      timestamp: now
    });

    return {
      ...state,
      metadata: {
        ...state.metadata,
        plan: {
          ...existingPlanMeta,
          todo: updatedTodos,
          todoMeta: {
            ...(existingPlanMeta.todoMeta || {}),
            updatedAt: now,
            source: 'heartbeat'
          }
        }
      }
    };
  }
};

// =============================================================================
// SECTION 5: MODULE EXPORTS
// =============================================================================

/**
 * Clean Stream Processor for AI Chat
 *
 * This is a complete rewrite of the stream handling logic with:
 * - Components-only message structure
 * - Pure functions for all event handlers
 * - No legacy code or backwards compatibility
 * - Single source of truth for state
 */

/**
 * Generate a unique message ID
 */
const generateMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Detect if content contains HTML (indicates slide content is being generated)
 */
const detectsHtmlContent = (content) => {
    if (!content || typeof content !== 'string') return false;
    
    // Look for common HTML patterns that indicate slide content
    const htmlPatterns = [
        /<\s*section/i,
        /<\s*div/i,
        /<\s*html/i,
        /<\s*!DOCTYPE/i,
        /<\s*h[1-6]/i,
        /<\s*p\s*>/i,
        /<\s*ul/i,
        /<\s*ol/i,
        /<\s*article/i,
        /<\s*header/i,
        /<\s*main/i
    ];
    
    return htmlPatterns.some(pattern => pattern.test(content));
};

/**
 * Count completed slide sections in HTML content
 * Uses </section> tags as slide boundaries
 */
const countCompletedSections = (content) => {
    if (!content || typeof content !== 'string') return 0;
    
    // Count closed section tags as completed slides
    const closedSections = (content.match(/<\/section>/gi) || []).length;
    return closedSections;
};

/**
 * Estimate slide creation progress based on streaming content
 * Analyzes HTML patterns and section tags to detect progress
 */
const estimateSlideProgress = (content, totalSlides) => {
    // First, check for HTML content and count completed sections
    const hasHtml = detectsHtmlContent(content);
    const completedSections = countCompletedSections(content);
    
    // If we have completed sections, use that as progress indicator
    if (completedSections > 0) {
        const slideIndex = Math.min(completedSections, totalSlides - 1);
        return {
            slideIndex,
            confidence: 0.9,
            hasHtmlContent: true,
            completedSections
        };
    }
    
    // If HTML is detected but no completed sections, we're on slide 0
    if (hasHtml) {
        return {
            slideIndex: 0,
            confidence: 0.8,
            hasHtmlContent: true,
            completedSections: 0
        };
    }
    
    // Fallback: Look for slide markers in text content
    const slideMarkers = [
        /(?:slide|Slide)\s*(\d+)/gi,
        /##\s*(\d+)/g,
        /(\d+)\.\s*[A-Z]/g, // Numbered headings
        /(?:creating|Creating|generating|Generating).*slide\s*(\d+)/gi
    ];

    let detectedSlide = 0;

    // Try to find explicit slide numbers
    for (const pattern of slideMarkers) {
        const matches = [...content.matchAll(pattern)];
        for (const match of matches) {
            const slideNum = parseInt(match[1]);
            if (slideNum && slideNum <= totalSlides) {
                detectedSlide = Math.max(detectedSlide, slideNum - 1); // Convert to 0-based index
            }
        }
    }

    // If no explicit markers found, estimate based on content length
    if (detectedSlide === 0 && content.length > 0) {
        // Assume each slide needs roughly 200-400 characters of content
        const averageSlideLength = 300;
        const estimatedSlideIndex = Math.min(
            Math.floor(content.length / averageSlideLength),
            totalSlides - 1
        );
        detectedSlide = Math.max(detectedSlide, estimatedSlideIndex);
    }

    // Cap at totalSlides - 1 (since we're 0-indexed)
    detectedSlide = Math.min(detectedSlide, totalSlides - 1);

    return {
        slideIndex: detectedSlide,
        confidence: detectedSlide > 0 ? 0.8 : 0.3,
        hasHtmlContent: false,
        completedSections: 0
    };
};

const formatLoopIntentLabel = (intent) => {
    if (!intent) {
        return 'Act';
    }
    const normalized = intent.toLowerCase();
    if (normalized === 'question') return 'Ask';
    if (normalized === 'completion') return 'Deliver';
    if (normalized === 'plan') return 'Plan';
    if (normalized === 'research') return 'Research';
    return intent.charAt(0).toUpperCase() + intent.slice(1);
};

const formatLoopStatusLabel = (status) => {
    if (!status) {
        return 'Update';
    }
    const normalized = status.toLowerCase();
    if (normalized === 'in_progress') return 'In progress';
    if (normalized === 'completed') return 'Completed';
    if (normalized === 'error') return 'Error';
    if (normalized === 'abandoned') return 'Abandoned';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

const selectLoopTools = (step = {}) => {
    if (Array.isArray(step.executedTools) && step.executedTools.length > 0) {
        return step.executedTools.filter(Boolean);
    }
    if (Array.isArray(step.toolCalls) && step.toolCalls.length > 0) {
        return step.toolCalls.filter(Boolean);
    }
    if (Array.isArray(step.toolMetrics) && step.toolMetrics.length > 0) {
        return step.toolMetrics
            .map(metric => metric?.tool)
            .filter(Boolean);
    }
    return [];
};

const normalizeLoopStep = (rawStep) => {
    if (!rawStep || typeof rawStep.stepNumber !== 'number') {
        return null;
    }

    const tools = selectLoopTools(rawStep);
    const intent = rawStep.intent || 'action';
    const status = rawStep.status || 'in_progress';
    const tag = rawStep.tag || (status === 'pending_clarification' ? 'question' : (intent === 'completion' ? 'completion' : 'action'));
    const toolMetrics = Array.isArray(rawStep.toolMetrics) ? rawStep.toolMetrics : [];
    const toolCalls = Array.isArray(rawStep.toolCalls) ? rawStep.toolCalls.filter(Boolean) : [];
    const executedTools = Array.isArray(rawStep.executedTools) ? rawStep.executedTools.filter(Boolean) : [];
    const primaryTool = rawStep.primaryTool || executedTools[executedTools.length - 1] || toolCalls[toolCalls.length - 1] || null;

    return {
        stepNumber: rawStep.stepNumber,
        intent,
        intentLabel: rawStep.intentLabel || formatLoopIntentLabel(intent),
        status,
        statusLabel: rawStep.statusLabel || formatLoopStatusLabel(status),
        tag,
        tools,
        primaryTool,
        startedAt: rawStep.startedAt || null,
        completedAt: rawStep.completedAt || null,
        durationMs: typeof rawStep.durationMs === 'number' ? rawStep.durationMs : null,
        queueLength: typeof rawStep.queueLength === 'number' ? rawStep.queueLength : null,
        toolMetrics,
        toolCalls,
        toolCallIds: Array.isArray(rawStep.toolCallIds) ? rawStep.toolCallIds.filter(Boolean) : []
    };
};

const buildLoopSummary = (rawSummary = {}, steps = []) => {
    const totalSteps = rawSummary.totalSteps ?? steps.length;
    const completedCount = rawSummary.completedCount ?? steps.filter(step => step.status === 'completed').length;
    const errorCount = rawSummary.errorCount ?? steps.filter(step => step.status === 'error').length;
    const pendingQuestions = rawSummary.pendingQuestions ?? steps.filter(step => step.tag === 'question' && step.status !== 'completed').length;
    const lastStepNumber = rawSummary.lastStepNumber ?? (steps.length > 0 ? steps[steps.length - 1].stepNumber : null);
    const lastTag = rawSummary.lastTag ?? (steps.length > 0 ? steps[steps.length - 1].tag : null);
    const lastUpdatedAt = rawSummary.lastUpdatedAt || new Date().toISOString();

    return {
        totalSteps,
        completedCount,
        errorCount,
        pendingQuestions,
        iterationsCompleted: rawSummary.iterationsCompleted ?? 0,
        maxIterations: rawSummary.maxIterations ?? 0,
        totalToolCalls: rawSummary.totalToolCalls ?? 0,
        totalToolRuntimeMs: rawSummary.totalToolRuntimeMs ?? 0,
        isComplete: rawSummary.isComplete ?? false,
        currentIntent: rawSummary.currentIntent || null,
        lastStepNumber,
        lastTag,
        lastUpdatedAt
    };
};

const STEP_INTENT_LABELS = {
    action: 'Action',
    plan: 'Outline',
    research: 'Research',
    question: 'Clarifying',
    completion: 'Deliver',
};

const deriveLoopStatusLabel = (step) => {
    if (!step) {
        return null;
    }

    if (typeof step.intent === 'string') {
        const normalizedIntent = step.intent.toLowerCase();
        if (STEP_INTENT_LABELS[normalizedIntent]) {
            return STEP_INTENT_LABELS[normalizedIntent];
        }
    }

    if (typeof step.tag === 'string') {
        const normalizedTag = step.tag.toLowerCase();
        if (STEP_INTENT_LABELS[normalizedTag]) {
            return STEP_INTENT_LABELS[normalizedTag];
        }
        if (normalizedTag === 'plan') {
            return 'Outline';
        }
        return normalizedTag.charAt(0).toUpperCase() + normalizedTag.slice(1);
    }

    if (typeof step.intentLabel === 'string' && step.intentLabel.trim()) {
        return step.intentLabel.trim();
    }

    if (typeof step.statusLabel === 'string' && step.statusLabel.trim()) {
        return step.statusLabel.trim();
    }

    return null;
};

/**
 * Component Update Utilities - Pure functions that return new arrays
 */


/**
 * Event Handlers - Pure functions that return new state
 */
export const eventHandlers = {
    connection: (event, state) => {
        console.log('🔌 SSE connection established:', {
            status: event.status,
            sessionId: event.sessionId
        });
        return state;
    },

    /**
     * Handle thinking events
     */
    thinking: (event, state) => {
        if (!event.content || !event.content.trim()) {
            console.log('⚠️ Thinking event has no content', {
              keys: Object.keys(event),
            });
            return state;
        }

        console.log('🧠 Processing thinking event, content length:', event.content.length);

        return {
            ...state,
            components: componentUpdaters.updateThinkingComponent(state.components, event.content),
            aiMessageId: state.aiMessageId || generateMessageId()
        };
    },

    /**
     * Handle content/message events
     * SIMPLIFIED: Just add text content, plans come through 'plan' event
     */
    content: (event, state) => {
        const isFinal = event.isFinal === true;

        // Defensive type checking - SDK can send objects/arrays
        if (!event.content) return state;
        if (typeof event.content !== 'string') {
            console.warn('⚠️ streamProcessor: Non-string content received, skipping:', typeof event.content);
            return state;
        }
        if (!event.content.trim()) return state;

        // Route text into the research card ONLY while a research sub-agent is
        // actively running. activeResearchAgentId is set by the subagent_start
        // telemetry event for research-analyst and cleared by subagent_complete.
        // No other path sets or clears it — so text can't bleed into the card
        // from the main agent's response after research ends.
        if (state.metadata?.activeResearchAgentId) {
            const captured = componentUpdaters.appendTextToResearchReport(state.components, event.content);
            if (captured) {
                return {
                    ...state,
                    components: captured,
                    aiMessageId: state.aiMessageId || generateMessageId(),
                    hasContent: true
                };
            }
            // No eligible card found — fall through to normal text handling
        }

        // Check if we're currently creating slides and update progress based on content
        let components = componentUpdaters.markThinkingComplete(state.components);
        const slideCreationIndex = components.findIndex(c => c.type === 'createSlides');

        if (slideCreationIndex >= 0 && state.metadata?.isCreatingSlides) {
            const slideCreationComponent = components[slideCreationIndex];
            const totalSlides = slideCreationComponent.data.count || 1;
            const existingProgressSlides = slideCreationComponent.data.progressSlides || [];

            // Check if any slide is currently marked as in-progress
            const hasInProgressSlide = existingProgressSlides.some(s => s.inProgress);

            if (totalSlides >= 1) {
                // Estimate progress based on content patterns and HTML detection
                const currentTextContent = components
                    .filter(c => c.type === 'text')
                    .map(c => c.content)
                    .join('');

                const combinedContent = currentTextContent + event.content;
                const estimatedProgress = estimateSlideProgress(combinedContent, totalSlides);

                // Trigger update if:
                // 1. Progress changed (new slide), OR
                // 2. HTML detected but no slide is marked as in-progress (start of generation)
                const progressChanged = estimatedProgress.slideIndex !== slideCreationComponent.data.completedCount;
                const needsInitialInProgress = estimatedProgress.hasHtmlContent && !hasInProgressSlide;

                if (progressChanged || needsInitialInProgress) {
                    const currentSlideIndex = estimatedProgress.slideIndex;
                    console.log(`🎯 Content-based progress: Slide ${currentSlideIndex + 1}/${totalSlides} (HTML: ${estimatedProgress.hasHtmlContent}, sections: ${estimatedProgress.completedSections})`);

                    // Build progress slides, preserving existing data where possible
                    const updatedProgressSlides = Array(totalSlides).fill().map((_, index) => {
                        const existing = existingProgressSlides[index] || {};
                        return {
                            ...existing,
                            slideIndex: index,
                            slideNumber: index + 1,
                            displayNumber: existing.displayNumber ?? (index + 1),
                            completed: index < currentSlideIndex,
                            inProgress: index === currentSlideIndex,
                            isPlaceholder: existing.isPlaceholder !== false
                        };
                    });

                    components = componentUpdaters.updateSlideCreationComponent(
                        components,
                        `Creating slides... ${currentSlideIndex}/${totalSlides} completed`,
                        totalSlides,
                        true,
                        null,
                        slideCreationComponent.data.slideIds || [],
                        slideCreationComponent.data.slideIndices || [],
                        currentSlideIndex,
                        updatedProgressSlides,
                        slideCreationComponent.data.toolId  // Preserve toolId
                    );
                }
            }
        }

        // Check if we should force a new text component
        // Force new if the last component is not text (e.g., after thinking, research, etc.)
        const lastComponent = components[components.length - 1];
        const forceNewText = lastComponent && lastComponent.type !== 'text';

        // Add text content
        components = componentUpdaters.updateTextComponent(
            components,
            event.content,
            forceNewText,
            { isFinal }
        );

        return {
            ...state,
            components,
            aiMessageId: state.aiMessageId || generateMessageId(),
            hasContent: true
        };
    },

    /**
     * Handle incremental content deltas for low-latency updates
     */
    content_delta: (event, state) => {
        if (!event.delta || typeof event.delta !== 'string' || event.delta.length === 0) {
            return state;
        }

        // Route delta into the research card ONLY while a research sub-agent is
        // actively running (see `content` handler above for the full rationale).
        if (state.metadata?.activeResearchAgentId) {
            const captured = componentUpdaters.appendTextToResearchReport(state.components, event.delta);
            if (captured) {
                return {
                    ...state,
                    components: captured,
                    aiMessageId: state.aiMessageId || generateMessageId(),
                    hasContent: true
                };
            }
        }

        let components = componentUpdaters.markThinkingComplete(state.components);

        const slideCreationIndex = components.findIndex(c => c.type === 'createSlides');
        if (slideCreationIndex >= 0 && state.metadata?.isCreatingSlides) {
            const slideCreationComponent = components[slideCreationIndex];
            const totalSlides = slideCreationComponent.data.count || 1;
            const existingProgressSlides = slideCreationComponent.data.progressSlides || [];

            // Check if any slide is currently marked as in-progress
            const hasInProgressSlide = existingProgressSlides.some(s => s.inProgress);

            if (totalSlides >= 1) {
                const currentTextContent = components
                    .filter(c => c.type === 'text')
                    .map(c => c.content)
                    .join('');

                const combinedContent = currentTextContent + event.delta;
                const estimatedProgress = estimateSlideProgress(combinedContent, totalSlides);

                // Trigger update if:
                // 1. Progress changed (new slide), OR
                // 2. HTML detected but no slide is marked as in-progress (start of generation)
                const progressChanged = estimatedProgress.slideIndex !== slideCreationComponent.data.completedCount;
                const needsInitialInProgress = estimatedProgress.hasHtmlContent && !hasInProgressSlide;

                if (progressChanged || needsInitialInProgress) {
                    const currentSlideIndex = estimatedProgress.slideIndex;
                    console.log(`🎯 Delta-based progress: Slide ${currentSlideIndex + 1}/${totalSlides} (HTML: ${estimatedProgress.hasHtmlContent}, sections: ${estimatedProgress.completedSections})`);

                    // Build progress slides, preserving existing data where possible
                    const updatedProgressSlides = Array(totalSlides).fill().map((_, index) => {
                        const existing = existingProgressSlides[index] || {};
                        return {
                            ...existing,
                            slideIndex: index,
                            slideNumber: index + 1,
                            displayNumber: existing.displayNumber ?? (index + 1),
                            completed: index < currentSlideIndex,
                            inProgress: index === currentSlideIndex,
                            isPlaceholder: existing.isPlaceholder !== false
                        };
                    });

                    components = componentUpdaters.updateSlideCreationComponent(
                        components,
                        `Creating slides... ${currentSlideIndex}/${totalSlides} completed`,
                        totalSlides,
                        true,
                        null,
                        slideCreationComponent.data.slideIds || [],
                        slideCreationComponent.data.slideIndices || [],
                        currentSlideIndex,
                        updatedProgressSlides,
                        slideCreationComponent.data.toolId  // Preserve toolId
                    );
                }
            }
        }

        components = componentUpdaters.appendTextDelta(components, event.delta);

        return {
            ...state,
            components,
            aiMessageId: state.aiMessageId || generateMessageId(),
            hasContent: true
        };
    },

    /**
     * Handle a content-block boundary from the SDK. Each assistant message
     * can contain multiple text/thinking/tool_use blocks (especially with
     * Opus 4.7 adaptive thinking, where [thinking, text, tool_use] or
     * [text, thinking, text, tool_use] patterns are common). The backend
     * forwards content_block_stop so we know to seal the trailing text
     * component — the next text delta will then create a fresh component
     * instead of merging into the previous block's content.
     */
    content_block_stop: (event, state) => {
        const components = state.components || [];
        if (components.length === 0) return state;
        const lastIdx = components.length - 1;
        const last = components[lastIdx];
        if (!last || last.type !== 'text' || last.streamingDelta !== true) {
            return state;
        }
        const { streamingDelta, ...rest } = last;
        const updated = [...components];
        updated[lastIdx] = rest;
        return { ...state, components: updated };
    },

    // message / clarification / clarification_resolved handlers removed:
    // backend never emits any of these event types. The alias `message` was
    // a forward for content events; direct `type: 'content'` covers it.
    // clarification / planDecision were part of plan-mode approval flow.

    /**
     * Handle session events (Claude Agent SDK session capture)
     */
    session: (event, state, context) => {
        // Extract session data from event
        const sessionId = event.sessionId;
        const model = event.model;
        const permissionMode = event.permissionMode;

        console.log('🔐 Session event received:', {
            sessionId,
            model,
            permissionMode,
            timestamp: event.timestamp
        });

        // Call onSessionCapture callback if provided
        if (typeof context?.onSessionCapture === 'function' && sessionId) {
            try {
                context.onSessionCapture(sessionId, {
                    model,
                    permissionMode,
                    timestamp: event.timestamp
                });
            } catch (error) {
                console.error('❌ Error in onSessionCapture:', error);
            }
        }

        return state;
    },

    /**
     * Handle tool call events
     */
    tool_call: (event, state, context) => {
        if (!event.toolCall) return state;

        // Clear research report capture mode when a new tool call arrives
        const rawToolName = event.toolCall.function?.name || 'tool';
        const toolName = normalizeMcpToolName(rawToolName);
        const connectedAppTool = resolveConnectedAppTool(rawToolName);
        let toolArgs = {};
        if (event.toolCall.function?.arguments) {
            try {
                toolArgs = JSON.parse(event.toolCall.function.arguments);
            } catch (parseError) {
                console.warn('🔧 Failed to parse tool arguments, continuing with {}:', parseError?.message);
                toolArgs = {};
            }
        }

        console.log('🔧 Processing tool call:', {
          toolName: rawToolName,
          argumentKeys: Object.keys(toolArgs),
        });

        let metadata = state.metadata || {};
        let metadataChanged = false;

        const setMetadata = (nextMetadata) => {
            metadata = nextMetadata;
            metadataChanged = true;
        };

        let components = state.components;

        switch (connectedAppTool ? '__connected_app__' : toolName) {
            case '__connected_app__': {
                const label = connectedToolDisplayLabel(toolArgs, connectedAppTool);
                components = componentUpdaters.markThinkingComplete(components);
                components = componentUpdaters.upsertConnectedAppWorkComponent(components, {
                    app: connectedAppTool.app,
                    toolId: event.toolCall.id,
                    toolName: connectedAppTool.tool,
                    label,
                    timestamp: event.timestamp
                });
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label,
                    connectedApp: connectedAppTool.app
                });
                break;
            }

            case 'WebSearch': {
                // Research card is ONLY maintained while a research sub-agent is
                // running. Inline WebSearch (main agent calling it directly) does
                // not get a research card — its results flow naturally into the
                // main agent's response in the main content area.
                if (state.metadata?.activeResearchAgentId) {
                    components = componentUpdaters.updateCitationsComponent(
                        components,
                        toolArgs.query || 'Researching...',
                        [],  // empty citations initially
                        true,  // isLoading
                        null,  // duration
                        'Starting research...',
                        []  // empty source summaries
                    );
                }
                break;
            }

            // Legacy web-slide-editor tool cases removed: generatePlan,
            // readPresentation, createSlides, editSlide (singular),
            // targetedEditSlide, regenerateSlide. None of these tool names
            // are registered in the backend's MCP server anymore —
            // plugin-only mode uses the mcp__powerpoint__* tools handled
            // below.

            // MCP tools: slide editing — aggregate into the unified slideWork card.
            // OOXML tools always carry a slide_index in args, so we can count
            // distinct slides touched in this turn.
            case 'edit_slide_xml':
            case 'edit_slide_chart':
            case 'edit_slide_text':
            case 'insert_icon': {
                const label = toolDisplayLabel(toolArgs, toolName, 'Editing slide');
                components = componentUpdaters.addSlideWorkUpdate(components, {
                    slideIndex: extractSlideIndex(toolArgs),
                    label
                });
                components = componentUpdaters.markThinkingComplete(components);

                setMetadata({
                    ...metadata,
                    statusLabel: label
                });
                break;
            }

            // MCP tools: read-only operations (inline status + collapse thinking)
            //
            // The Locker's four retrieval tools are read-only in exactly this
            // sense and get exactly this card. They carry no `explanation`
            // argument, so the label is the static one from MCP_TOOL_LABELS,
            // and repeated calls merge into the usual `×N` count.
            case 'read_deck_content':
            case 'read_slide_text':
            case 'list_slide_shapes':
            case 'search_icons':
            case 'search_locker':
            case 'find_locker_exact':
            case 'list_locker_documents':
            case 'fetch_locker_citations': {
                const label = toolDisplayLabel(toolArgs, toolName, `Running ${toolName}`);
                components = componentUpdaters.markThinkingComplete(components);
                components = componentUpdaters.addToolStatusComponent(
                    components, event.toolCall.id, toolName,
                    label
                );
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label
                });
                break;
            }

            // EXCEL AND WORD: read-only tools.
            //
            // Same treatment PowerPoint's read-only tools get: an inline status
            // row that merges repeat calls into `×N`. Tool names come from
            // createExcelMcpServer / createWordMcpServer.
            case 'get_initial_state':
            case 'get_cell_ranges':
            case 'get_range_as_csv':
            case 'search_data':
            case 'screenshot_range':
            case 'get_all_objects':
            case 'read_doc_section':
            case 'verify_doc':
            case 'screenshot_document':
            case 'explain_edits':
            case 'read_skill': {
                const label = toolDisplayLabel(toolArgs, toolName, `Running ${toolName}`);
                components = componentUpdaters.markThinkingComplete(components);
                components = componentUpdaters.addToolStatusComponent(
                    components, event.toolCall.id, toolName,
                    label
                );
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label
                });
                break;
            }

            // EXCEL AND WORD: tools that MODIFY the file.
            //
            // These rendered nothing at all: the switch had no case for any of
            // them, so a turn that created sheets, built a Sources tab and
            // edited Assumptions produced one card, and that card was
            // PowerPoint's. They get a named row each rather than being folded
            // into the aggregate card, because a workbook has no equivalent of
            // a slide index to aggregate on: "Updating cells ×12" is the whole
            // of what the pane can honestly say, and it is what the user was
            // missing. Repeat calls to the same tool merge, and the model's own
            // `explanation` argument names the individual call when it sends
            // one, exactly as it does on the PowerPoint rows.
            case 'set_cell_range':
            case 'clear_cell_range':
            case 'copy_to':
            case 'modify_sheet_structure':
            case 'modify_workbook_structure':
            case 'resize_range':
            case 'modify_object':
            case 'edit_doc_text':
            case 'edit_doc_list':
            case 'collapse_blank_paragraphs':
            case 'propose_doc_edits':
            case 'apply_template': {
                const label = toolDisplayLabel(toolArgs, toolName, `Running ${toolName}`);
                components = componentUpdaters.markThinkingComplete(components);
                components = componentUpdaters.addToolStatusComponent(
                    components, event.toolCall.id, toolName,
                    label
                );
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label
                });
                break;
            }

            // MCP tools: theme editing — affects the whole deck (no specific slide
            // index) so we route to slideWork with slideIndex: null.
            case 'edit_slide_master': {
                const label = toolDisplayLabel(toolArgs, toolName, 'Editing theme');
                components = componentUpdaters.addSlideWorkUpdate(components, {
                    slideIndex: null,
                    label
                });
                components = componentUpdaters.markThinkingComplete(components);
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label
                });
                break;
            }

            // MCP tools: generic Office.js execution. Read-only probes show as
            // inline status; everything else aggregates into the slideWork card.
            // We can't infer slide_index from the opaque code body — slideWork
            // gracefully renders "Working on slide" until an OOXML tool fires
            // with an extractable index.
            case 'execute_office_js': {
                const explanation = toolArgs.explanation || '';
                const hostLabel = officeJsLabel(context?.app);
                const isReadOnly = /\b(read|get|list|count|check|navigate|go\s*to|find|select|current)\b/i.test(explanation)
                    && !/\b(set|change|modify|update|add|delete|remove|insert|create|write|replace|format|style|move|resize)\b/i.test(explanation);

                if (isReadOnly) {
                    components = componentUpdaters.addToolStatusComponent(
                        components, event.toolCall.id, toolName,
                        explanation.slice(0, 50) || hostLabel
                    );
                } else {
                    components = componentUpdaters.addSlideWorkUpdate(components, {
                        slideIndex: null,
                        label: explanation.slice(0, 60) || hostLabel,
                        app: context?.app || null
                    });
                }
                components = componentUpdaters.markThinkingComplete(components);

                setMetadata({
                    ...metadata,
                    statusLabel: explanation.slice(0, 50) || hostLabel,
                    statusHint: explanation.slice(0, 50) || null
                });
                break;
            }

            // MCP tools: verification/screenshot (inline status)
            case 'screenshot_slide':
            case 'verify_slides':
            case 'verify_slide_visual':
            case 'ask_user_question': {
                const label = toolDisplayLabel(toolArgs, toolName, 'Verifying');
                components = componentUpdaters.addToolStatusComponent(
                    components, event.toolCall.id, toolName,
                    label
                );
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label
                });
                break;
            }

            // MCP tools: duplicate slide — modifies the deck, route to slideWork
            // with the source slide's index so it counts toward the touched set.
            case 'duplicate_slide': {
                const label = toolDisplayLabel(toolArgs, toolName, 'Duplicating slide');
                components = componentUpdaters.addSlideWorkUpdate(components, {
                    slideIndex: extractSlideIndex(toolArgs),
                    label
                });
                components = componentUpdaters.markThinkingComplete(components);
                setMetadata({
                    ...metadata,
                    statusLabel: label,
                    statusHint: label
                });
                break;
            }

            // SDK platform tools: web fetch
            case 'WebFetch':
            case 'web_fetch': {
                // Record the source first. The status bullet below merges into
                // the tail component, so opening the research card after it
                // would split one `Fetching webpage ×20` bullet into two.
                components = componentUpdaters.addFetchedPage(components, {
                    url: toolArgs.url,
                    toolId: event.toolCall.id,
                    timestamp: event.timestamp
                });
                // The bullet only counts fetches; the card above names them.
                components = componentUpdaters.addToolStatusComponent(
                    components, event.toolCall.id, toolName,
                    'Fetching webpage'
                );
                components = componentUpdaters.markThinkingComplete(components);
                setMetadata({
                    ...metadata,
                    statusLabel: 'Fetching webpage',
                    statusHint: 'Fetching webpage'
                });
                break;
            }

            // MCP tools: web search (same gating as WebSearch above — only when a
            // research sub-agent is active does a research card get created).
            case 'web_search': {
                if (state.metadata?.activeResearchAgentId) {
                    components = componentUpdaters.updateCitationsComponent(
                        components,
                        toolArgs.query || 'Researching...',
                        [],
                        true,
                        null,
                        'Starting research...',
                        []
                    );
                }
                break;
            }

            default:
                console.log('ℹ️ Unhandled tool call:', toolName);
                break;

        }

        if (!metadataChanged && components === state.components) {
            return state;
        }

        return {
            ...state,
            components,
            metadata,
            aiMessageId: state.aiMessageId || generateMessageId()
        };
    },

    /**
     * Handle slides creation completion
     */
    slides: (event, state, context) => handleSlidesEvent(event, state, context, componentUpdaters),


    /**
     * Handle individual slide progress during creation
     */
    slide_progress: (event, state) => handleSlideProgressEvent(event, state, componentUpdaters),


    /**
     * Handle WebSearch queries for research transparency
     */
    search_query: (event, state) => {
        if (!event.query) {
            return state;
        }

        console.log('🔍 WebSearch query received:', {
          queryLength: event.query.length,
        });

        // Add query to metadata search queries array (immutably). Mark every
        // previous entry as completed — only the newly arriving search is
        // active. This drives the per-bar shimmer in AiChatCards so the
        // previous bar stops glowing the instant the next one opens.
        const previousQueries = (state.metadata?.searchQueries || []).map(q => ({
            ...q,
            completed: true
        }));
        const searchQueries = [
            ...previousQueries,
            {
                query: event.query,
                toolId: event.toolId,
                timestamp: event.timestamp || new Date().toISOString(),
                completed: false
            }
        ];

        console.log('🔍 [search_query handler] After adding query:', {
            previousCount: (state.metadata?.searchQueries || []).length,
            newCount: searchQueries.length,
            latestQueryLength: event.query.length
        });

        // Update or create research component to show queries in real-time
        let components = [...state.components];
        const existingIndex = components.findIndex(c => c.type === 'research');

        if (existingIndex >= 0) {
            // Update existing research component with new search query
            components[existingIndex] = {
                ...components[existingIndex],
                data: {
                    ...components[existingIndex].data,
                    searchQueries: [...searchQueries],
                    isLoading: true,
                    status: 'Searching...'
                },
                timestamp: new Date().toISOString()
            };
        } else {
            // Create new research component if none exists
            components.push({
                type: 'research',
                data: {
                    query: state.metadata?.researchQuery || 'Research',
                    keyFindings: [],
                    fullReport: '',
                    report: '',
                    reportDuration: null,
                    citations: [],
                    citationsDuration: null,
                    sourceSummaries: [],
                    searchQueries: [...searchQueries],
                    isLoading: true,
                    status: 'Searching...'
                },
                timestamp: new Date().toISOString()
            });
        }

        const newState = {
            ...state,
            components,
            metadata: {
                ...state.metadata,
                action: 'research',
                searchQueries
            }
        };

        console.log('🔍 [search_query handler] Returning state with searchQueries:', {
            metadataSearchQueriesCount: newState.metadata.searchQueries?.length || 0,
            componentSearchQueriesCount: components.find(c => c.type === 'research')?.data?.searchQueries?.length || 0
        });

        return newState;
    },

    /**
     * Handle citations
     */
    citations: (event, state) => handleCitationsEvent(event, state, componentUpdaters),


    /**
     * Handle research report (streaming)
     */
    report: (event, state) => handleReportEvent(event, state, componentUpdaters),


    /**
     * Handle status messages
     */
    status: (event, state, context) => handleStatusEvent(event, state, componentUpdaters, context),



    // REMOVED: slide_lock, slide_patch, manifest_patch handlers
    // (web slide editor removed - Restack is now plugin-only)


    /**
     * Handle Office.js code execution events (PowerPoint plugin).
     * Backend sends JavaScript code that gets evaluated inside PowerPoint.run().
     */
    office_code: (event, state, context) => {
        if (typeof context?.onOfficeCode === 'function' && event.code) {
            try { context.onOfficeCode(event.code, event.meta); } catch (_) { }
        }
        return state;
    },

    /**
     * Mid-turn user message accepted into the running turn (steering). Render
     * the user's text as an inline bubble inside the live stream: repository
     * writes made mid-turn are not reliably reflected while a turn renders,
     * and this also persists the text with the AI message in order.
     */
    user_message_queued: (event, state) => {
        const content = typeof event.content === 'string' ? event.content.trim() : '';
        if (!content) return state;
        return {
            ...state,
            components: [
                ...state.components,
                {
                    type: 'steered_user_message',
                    data: { content, mode: event.mode || 'steered' },
                    timestamp: event.timestamp || new Date().toISOString()
                }
            ]
        };
    },

    /**
     * Handle two-way tool call requests from the backend MCP server.
     * The backend sends a callId + code; frontend executes in PowerPoint
     * and POSTs the result back to /sessions/:id/tool-results.
     */
    tool_call_request: (event, state, context) => {
        if (typeof context?.onToolCallRequest === 'function' && event.callId) {
            try { context.onToolCallRequest(event.callId, event.toolName, event.args); } catch (_) { }
        }
        const label = event.args?.explanation || MCP_TOOL_LABELS[event.toolName] || null;
        if (!label) return state;
        return {
            ...state,
            components: componentUpdaters.markThinkingComplete(state.components),
            metadata: { ...state.metadata, statusHint: label, statusLabel: label }
        };
    },

    // permission_request handler removed: backend runs permissionMode: 'dontAsk'
    // with a fixed allowedTools allowlist, so canUseTool / permission_request
    // events are never emitted. If we reintroduce user-gated tool approvals,
    // re-add this branch.

    telemetry: (event, state, context) => {
        let nextState = state;

        if (event.namespace === 'loop') {
            const rawStep = event.data?.step;
            const normalizedStep = normalizeLoopStep(rawStep);
            const previousSteps = state.metadata?.loop?.steps || [];
            const stepsMap = new Map(previousSteps.map(step => [step.stepNumber, step]));

            if (normalizedStep) {
                const existing = stepsMap.get(normalizedStep.stepNumber) || {};
                stepsMap.set(normalizedStep.stepNumber, {
                    ...existing,
                    ...normalizedStep
                });
            }

            const steps = Array.from(stepsMap.values()).sort((a, b) => a.stepNumber - b.stepNumber);
            const summary = buildLoopSummary(event.data?.summary, steps);

            const activeStep = [...steps].reverse().find(step => {
                const status = (step.status || '').toLowerCase();
                return status && status !== 'completed' && status !== 'abandoned' && status !== 'cancelled';
            });

            const latestCompletedStep = [...steps].reverse().find(step => (step.status || '').toLowerCase() === 'completed');

            let statusLabelOverride = null;
            let statusHintOverride = undefined;

            if (activeStep) {
                statusLabelOverride = deriveLoopStatusLabel(activeStep) || statusLabelOverride;
                if (activeStep.statusLabel) {
                    statusHintOverride = activeStep.statusLabel;
                }
            } else if (latestCompletedStep) {
                const completedLabel = deriveLoopStatusLabel(latestCompletedStep);
                if (completedLabel) {
                    statusLabelOverride = `${completedLabel} complete`;
                }
                if (latestCompletedStep.statusLabel) {
                    statusHintOverride = latestCompletedStep.statusLabel;
                }
            }

            nextState = {
                ...state,
                components: componentUpdaters.updateLoopTimelineComponent(state.components, {
                    steps,
                    summary
                }),
                metadata: {
                    ...state.metadata,
                    loop: {
                        summary,
                        steps
                    },
                    statusLabel: statusLabelOverride || state.metadata?.statusLabel,
                    statusHint: statusHintOverride !== undefined
                        ? statusHintOverride
                        : state.metadata?.statusHint
                }
            };
        }

        if (event.namespace === 'guard') {
            const guardData = event.data || {};
            const previousMetadata = nextState.metadata || state.metadata || {};

            // Simplified guard state - backend only sends {status, reason}
            const guard = {
                status: guardData.status || 'idle',
                satisfied: guardData.status === 'satisfied',
                blocked: guardData.status === 'blocked',
                reason: guardData.reason || null,
                lastStatusChange: guardData.lastStatusChange || new Date().toISOString()
            };

            nextState = {
                ...nextState,
                metadata: {
                    ...previousMetadata,
                    guard
                }
            };
        }

        // 'tool' namespace telemetry: feed TodoChecklist so tool activity cards
        // appear, and on tool_complete mark the corresponding card / search bar
        // completed (this is what stops the WebSearch shimmer).
        //
        // PREVIOUSLY this called eventHandlers.tool_telemetry(...), but that
        // handler was deleted (see "tool_telemetry handler removed" comment
        // below). The orphaned call threw silently inside processEvent's
        // try/catch and returned unchanged state — meaning completeToolCard
        // never fired, leaving the search shimmer on forever.
        if (event.namespace === 'tool') {
            const toolEvent = event.data?.event;
            const rawTelemetryTool = event.data?.tool;
            const connectedAppTool = resolveConnectedAppTool(rawTelemetryTool);
            const normalizedTelemetryTool = connectedAppTool?.tool || normalizeMcpToolName(rawTelemetryTool);
            if (toolEvent === 'tool_start' || toolEvent === 'tool_heartbeat' || toolEvent === 'tool_complete') {
                nextState = componentUpdaters.upsertTelemetryTask(nextState, {
                    event: toolEvent,
                    tool: normalizedTelemetryTool,
                    toolId: event.data?.toolId,
                    timestamp: event.timestamp,
                    success: event.data?.success !== false
                });
            }

            if (connectedAppTool && (toolEvent === 'tool_start' || toolEvent === 'tool_complete')) {
                const toolId = event.data?.toolId;
                const alreadyTracked = nextState.components.some(component =>
                    component.type === 'connectedAppWork' &&
                    Array.isArray(component.data?.steps) &&
                    component.data.steps.some(step => step.toolId === toolId)
                );
                let connectedComponents = nextState.components;
                if (!alreadyTracked) {
                    connectedComponents = componentUpdaters.upsertConnectedAppWorkComponent(connectedComponents, {
                        app: connectedAppTool.app,
                        toolId,
                        toolName: connectedAppTool.tool,
                        label: connectedToolDisplayLabel({}, connectedAppTool),
                        timestamp: event.timestamp
                    });
                }
                if (toolEvent === 'tool_complete') {
                    const rawError = event.data?.error;
                    const errorMessage = typeof rawError === 'string'
                        ? rawError
                        : rawError?.message;
                    connectedComponents = componentUpdaters.completeConnectedAppWorkComponent(
                        connectedComponents,
                        toolId,
                        event.data?.success !== false,
                        errorMessage || null
                    );
                }
                nextState = {
                    ...nextState,
                    components: componentUpdaters.markThinkingComplete(connectedComponents)
                };
            }
            if (toolEvent === 'tool_complete') {
                nextState = componentUpdaters.completeToolCard(nextState, normalizedTelemetryTool, event.data?.toolId);
                nextState = {
                    ...nextState,
                    components: componentUpdaters.completeToolStatusComponent(
                        nextState.components, event.data?.toolId, event.data?.success !== false
                    )
                };
            }
            return nextState;
        }

        // Handle compaction telemetry (from PreCompact hook and compact_boundary)
        if (event.namespace === 'compaction') {
            const compactionEvent = event.data?.event;
            const isCompacting = compactionEvent === 'compaction_start';

            console.log('🗜️ Compaction Telemetry:', {
                event: compactionEvent,
                trigger: event.data?.trigger,
                isCompacting
            });

            nextState = {
                ...nextState,
                metadata: {
                    ...nextState.metadata,
                    compaction: {
                        isCompacting,
                        event: compactionEvent,
                        trigger: event.data?.trigger || 'auto',
                        priorTokens: event.data?.priorTokens,
                        compactedTokens: event.data?.compactedTokens,
                        reduction: event.data?.reduction,
                        timestamp: event.data?.timestamp || new Date().toISOString()
                    }
                }
            };

            // If using context callback, notify it about compaction state
            if (context?.onCompactionStateChange) {
                context.onCompactionStateChange(isCompacting, event.data);
            }

            return nextState;
        }

        // Handle SDK hook telemetry. Note: we intentionally do NOT store
        // sdkTelemetry history or lastSdkEvent on metadata — they were
        // set-only (never read) and accumulated unboundedly across turns.
        if (event.namespace === 'sdk') {
            console.log('🔌 SDK Hook Telemetry:', {
                event: event.data?.event,
                tool: event.data?.tool,
                toolId: event.data?.toolId
            });

            // Ingest tool_start, tool_heartbeat, and tool_complete events into todo checklist
            const sdkEvent = event.data?.event;
            if (sdkEvent === 'tool_start' || sdkEvent === 'tool_heartbeat' || sdkEvent === 'tool_complete') {
                const normalizedTool = normalizeMcpToolName(event.data?.tool);
                console.log('📋 Ingesting SDK telemetry into todo checklist:', {
                    event: sdkEvent,
                    tool: normalizedTool,
                    toolId: event.data?.toolId,
                    success: event.data?.success
                });

                nextState = componentUpdaters.upsertTelemetryTask(nextState, {
                    event: sdkEvent,
                    tool: normalizedTool,
                    toolId: event.data?.toolId,
                    timestamp: event.timestamp,
                    success: event.data?.success !== false
                });
                if (sdkEvent === 'tool_complete') {
                    nextState = componentUpdaters.completeToolCard(nextState, normalizedTool, event.data?.toolId);
                    nextState = {
                        ...nextState,
                        components: componentUpdaters.completeToolStatusComponent(
                            nextState.components, event.data?.toolId, event.data?.success !== false
                        )
                    };
                }
            }
        }

        // Handle subagent telemetry
        if (event.namespace === 'subagent') {
            const subagentData = event.data || {};
            const subagentEvent = subagentData.event;
            const agentId = subagentData.agentId;
            const agentLabel = subagentData.agentLabel || agentId;
            const toolId = subagentData.toolId;
            const description = subagentData.description;

            console.log('🤖 Subagent Telemetry:', {
                event: subagentEvent,
                agentId,
                agentLabel,
                toolId,
                description
            });

            // Initialize subagent tracking in metadata
            const subagents = nextState.metadata?.subagents || {
                active: {},
                history: []
            };

            if (subagentEvent === 'subagent_start') {
                // Track active subagent
                subagents.active[toolId] = {
                    agentId,
                    agentLabel,
                    description,
                    startedAt: subagentData.timestamp || new Date().toISOString()
                };

                // SPECIAL CASE: slide-builder → seed a slideWork card so the
                // unified slide work surface shows up immediately, before any
                // of the subagent's internal tool calls land. Subsequent OOXML
                // tool calls (if surfaced) merge into this same card.
                if (agentId === 'slide-builder') {
                    const newComponents = componentUpdaters.addSlideWorkUpdate(
                        nextState.components,
                        { slideIndex: null, label: 'Working on slides' }
                    );

                    nextState = {
                        ...nextState,
                        components: newComponents,
                        metadata: {
                            ...nextState.metadata,
                            subagents,
                            statusLabel: description
                                ? `Slide builder: ${description}`
                                : 'Slide builder working...',
                            statusHint: description || null,
                            slideCreationStartTime: Date.now()
                        }
                    };
                } else if (agentId === 'research-analyst') {
                    // RESEARCH-ANALYST: seed the research card so "Researching..."
                    // shows up immediately (don't wait for the sub-agent's first
                    // web_search tool call), and flip on activeResearchAgentId —
                    // the single flag that gates whether content events route
                    // into the research card.
                    const seededComponents = componentUpdaters.updateCitationsComponent(
                        nextState.components,
                        description || 'Researching...',
                        [],    // empty citations initially
                        true,  // isLoading
                        null,  // duration
                        'Starting research...',
                        []     // empty source summaries
                    );

                    nextState = {
                        ...nextState,
                        components: seededComponents,
                        metadata: {
                            ...nextState.metadata,
                            subagents,
                            statusLabel: `${agentLabel} working...`,
                            statusHint: description || null,
                            activeSubagent: { agentId, agentLabel, description },
                            activeResearchAgentId: toolId,
                            researchStartTime: Date.now(),
                            researchQuery: description || nextState.metadata?.researchQuery || 'Research'
                        }
                    };
                } else {
                    // OTHER SUBAGENTS: Keep existing behavior (just metadata tracking, no component)
                    nextState = {
                        ...nextState,
                        metadata: {
                            ...nextState.metadata,
                            subagents,
                            statusLabel: `${agentLabel} working...`,
                            statusHint: description || null,
                            activeSubagent: {
                                agentId,
                                agentLabel,
                                description
                            }
                        }
                    };
                }

            } else if (subagentEvent === 'subagent_complete') {
                // Mark subagent as completed
                const activeSubagent = subagents.active[toolId];
                if (activeSubagent) {
                    // Move to history
                    subagents.history.push({
                        ...activeSubagent,
                        completedAt: subagentData.timestamp || new Date().toISOString(),
                        duration: subagentData.duration,
                        resultLength: subagentData.resultLength,
                        success: true
                    });
                    delete subagents.active[toolId];
                }

                // Clear active subagent from status if it was showing
                const remainingActive = Object.values(subagents.active);
                const nextActiveSubagent = remainingActive.length > 0
                    ? remainingActive[0]
                    : null;

                nextState = {
                    ...nextState,
                    metadata: {
                        ...nextState.metadata,
                        subagents,
                        statusLabel: nextActiveSubagent
                            ? `${nextActiveSubagent.agentLabel} working...`
                            : nextState.metadata?.statusLabel,
                        statusHint: nextActiveSubagent
                            ? nextActiveSubagent.description
                            : nextState.metadata?.statusHint,
                        activeSubagent: nextActiveSubagent
                    }
                };

                // Update subagent component to completed
                nextState = {
                    ...nextState,
                    components: componentUpdaters.updateSubagentComponent(
                        nextState.components,
                        toolId,
                        {
                            status: 'completed',
                            duration: subagentData.duration,
                            resultLength: subagentData.resultLength,
                            timestamp: subagentData.timestamp
                        }
                    )
                };

                // If the completing sub-agent is the one we're capturing research
                // from, stop capturing and finalize the card. Any text the main
                // agent emits after this point routes to the main content area.
                if (nextState.metadata?.activeResearchAgentId === toolId) {
                    console.log('🔬 research sub-agent completed — closing research card capture');
                    nextState = {
                        ...nextState,
                        components: componentUpdaters.finalizeResearchReport(nextState.components),
                        metadata: {
                            ...nextState.metadata,
                            activeResearchAgentId: null
                        }
                    };
                }

            } else if (subagentEvent === 'subagent_error') {
                // Mark subagent as failed
                const activeSubagent = subagents.active[toolId];
                if (activeSubagent) {
                    // Move to history with error
                    subagents.history.push({
                        ...activeSubagent,
                        completedAt: subagentData.timestamp || new Date().toISOString(),
                        success: false,
                        error: subagentData.error
                    });
                    delete subagents.active[toolId];
                }

                // Clear active subagent from status if it was showing
                const remainingActive = Object.values(subagents.active);
                const nextActiveSubagent = remainingActive.length > 0
                    ? remainingActive[0]
                    : null;

                nextState = {
                    ...nextState,
                    metadata: {
                        ...nextState.metadata,
                        subagents,
                        statusLabel: nextActiveSubagent
                            ? `${nextActiveSubagent.agentLabel} working...`
                            : nextState.metadata?.statusLabel,
                        statusHint: nextActiveSubagent
                            ? nextActiveSubagent.description
                            : nextState.metadata?.statusHint,
                        activeSubagent: nextActiveSubagent
                    }
                };

                // Update subagent component to error
                nextState = {
                    ...nextState,
                    components: componentUpdaters.updateSubagentComponent(
                        nextState.components,
                        toolId,
                        {
                            status: 'error',
                            error: subagentData.error,
                            timestamp: subagentData.timestamp
                        }
                    )
                };

                // Same cleanup as subagent_complete: if the failing sub-agent was
                // our active research source, stop capturing so follow-up main-
                // agent text lands in the main content area.
                if (nextState.metadata?.activeResearchAgentId === toolId) {
                    nextState = {
                        ...nextState,
                        components: componentUpdaters.finalizeResearchReport(nextState.components),
                        metadata: {
                            ...nextState.metadata,
                            activeResearchAgentId: null
                        }
                    };
                }
            }
        }

        return nextState;
    },

    // tool_telemetry handler removed: backend never emits a standalone
    // `tool_telemetry` event type — tool telemetry goes through the generic
    // `telemetry` event with namespace 'tool' and is handled there. The old
    // handler was accumulating metadata.toolTelemetry unboundedly per turn.

    // plan handler removed: plan mode is gone. Backend's sendPlan() is
    // unused and being deleted. If plan mode is reintroduced later, re-add
    // this handler with a cleaner implementation.

    todoList: (event, state) => handlePlanTodosEvent(event, state, componentUpdaters, generateMessageId),


    /**
     * Handle completion
     */
    complete: async (event, state, context) => {
        console.log('✅ Processing complete event', {
            hasResponse: !!event.response,
            hasSlides: !!(event.response?.slides),
            slideCount: event.response?.slides?.length,
            hasResearchData: !!event.response?.researchData
        });

        // Mark thinking as complete
        let components = componentUpdaters.markThinkingComplete(state.components);
        components = componentUpdaters.settleConnectedAppWorkComponents(components, true);

        // Thinking content is already handled during streaming
        // Don't add it again here to avoid duplication

        // Check if complete event contains slides in the response
        if (event.response?.slides && event.response.slides.length > 0) {
            console.log('📝 Complete event contains slides, processing them');

            try {
                // Process the slides using the slides event handler with proper async handling
                const slidesEventResult = await eventHandlers.slides(
                    { slides: event.response.slides },
                    { ...state, components },
                    context
                );

                // Update components and state from slides processing
                components = slidesEventResult.components;
                state = { ...state, ...slidesEventResult };
            } catch (error) {
                console.error('❌ Error processing slides in complete event:', error);
                // Continue with completion even if slide processing fails
            }
        }

        if (event.response?.planTelemetry) {
            const responseTelemetry = event.response.planTelemetry;
            const responseSnapshots = Array.isArray(responseTelemetry.snapshots)
                ? responseTelemetry.snapshots
                : [];
            const latestSnapshot = responseSnapshots.length > 0
                ? {
                    ...responseSnapshots[responseSnapshots.length - 1],
                    stage: responseSnapshots[responseSnapshots.length - 1].stage || 'complete'
                }
                : {
                    stage: 'complete',
                    queueLength: responseTelemetry.queueLength ?? 0,
                    directives: responseTelemetry.directives || [],
                    reminders: responseTelemetry.reminders ?? 0,
                    autoInvokes: responseTelemetry.autoInvokes ?? 0,
                    timestamp: new Date().toISOString()
                };

            const telemetry = {
                latest: latestSnapshot,
                snapshots: responseSnapshots.length > 0 ? responseSnapshots : [latestSnapshot],
                reminders: responseTelemetry.reminders ?? latestSnapshot.reminders ?? 0,
                autoInvokes: responseTelemetry.autoInvokes ?? latestSnapshot.autoInvokes ?? 0,
                queueLength: latestSnapshot.queueLength ?? responseTelemetry.queueLength ?? 0,
                directives: latestSnapshot.directives || responseTelemetry.directives || []
            };

            if (typeof context?.onPlanTelemetry === 'function') {
                context.onPlanTelemetry({
                    source: 'complete',
                    ...telemetry
                });
            }

            state = {
                ...state,
                metadata: {
                    ...state.metadata,
                    planTelemetry: telemetry
                }
            };
        }

        if (event.response?.researchData) {
            const responseResearch = event.response.researchData;
            const responseCitations = Array.isArray(responseResearch.citations)
                ? responseResearch.citations
                : (Array.isArray(responseResearch.sources) ? responseResearch.sources : []);
            const responseKeyFindings = Array.isArray(responseResearch.keyFindings)
                ? responseResearch.keyFindings
                : [];
            const responseFullReport = typeof responseResearch.fullReport === 'string'
                ? responseResearch.fullReport
                : (typeof responseResearch.report === 'string' ? responseResearch.report : '');
            const responseSourceSummaries = Array.isArray(responseResearch.sourceSummaries)
                ? responseResearch.sourceSummaries
                : [];
            const responseSearchQueries = Array.isArray(responseResearch.searchQueries)
                ? responseResearch.searchQueries
                : (state.metadata?.searchQueries || []);
            const responseQuery = responseResearch.query
                || responseResearch.researchQuery
                || state.metadata?.researchQuery
                || 'Research';
            const researchDuration = state.metadata?.researchStartTime
                ? `${Math.floor((Date.now() - state.metadata.researchStartTime) / 60000)}m ${Math.floor(((Date.now() - state.metadata.researchStartTime) % 60000) / 1000)}s`
                : (state.metadata?.reportDuration || state.metadata?.citationsDuration || null);

            if (responseCitations.length > 0 || responseSourceSummaries.length > 0) {
                components = componentUpdaters.updateCitationsComponent(
                    components,
                    responseQuery,
                    responseCitations,
                    false,
                    researchDuration,
                    'Research ready',
                    responseSourceSummaries
                );
            }

            if ((typeof responseFullReport === 'string' && responseFullReport.trim().length > 0) || responseKeyFindings.length > 0) {
                components = componentUpdaters.updateReportComponent(
                    components,
                    responseQuery,
                    responseKeyFindings,
                    responseFullReport,
                    researchDuration,
                    'Research ready',
                    responseSourceSummaries,
                    responseSearchQueries
                );
            }

            state = {
                ...state,
                metadata: {
                    ...state.metadata,
                    action: 'research',
                    researchQuery: responseQuery,
                    citations: responseCitations.length > 0 ? responseCitations : (state.metadata?.citations || []),
                    citationsDuration: responseCitations.length > 0
                        ? (researchDuration || state.metadata?.citationsDuration || null)
                        : state.metadata?.citationsDuration,
                    sourceSummaries: responseSourceSummaries.length > 0
                        ? responseSourceSummaries
                        : (state.metadata?.sourceSummaries || []),
                    keyFindings: responseKeyFindings.length > 0
                        ? responseKeyFindings
                        : state.metadata?.keyFindings,
                    fullReport: responseFullReport || state.metadata?.fullReport,
                    researchReport: responseFullReport || state.metadata?.researchReport,
                    reportDuration: responseFullReport
                        ? (researchDuration || state.metadata?.reportDuration || null)
                        : state.metadata?.reportDuration,
                    searchQueries: responseSearchQueries
                }
            };
        }

        // Complete any pending slide edits
        if (state.metadata?.editedSlidesCount > 0) {
            const editIndex = components.findIndex(c => c.type === 'editSlides');
            if (editIndex >= 0) {
                const updated = [...components];
                updated[editIndex] = {
                    ...updated[editIndex],
                    data: {
                        ...updated[editIndex].data,
                        status: `✅ Updated ${state.metadata.editedSlidesCount} slide${state.metadata.editedSlidesCount > 1 ? 's' : ''}`,
                        isLoading: false
                    }
                };
                components = updated;
            }
        }

        // Clean up UI state
        if (context?.completeStream) {
            context.completeStream();
        }

        const endTime = Date.now();
        const durationSeconds = state.metadata?.startTime
            ? Math.max(0, Math.floor((endTime - state.metadata.startTime) / 1000))
            : state.metadata?.durationSeconds || 0;

        // Simplified guard state - use response guard if provided, otherwise keep existing
        const guardFromResponse = event.response?.guard;
        const mergedGuard = guardFromResponse
            ? {
                status: guardFromResponse.status || 'idle',
                satisfied: guardFromResponse.status === 'satisfied',
                blocked: guardFromResponse.status === 'blocked',
                reason: guardFromResponse.reason || null,
                lastStatusChange: guardFromResponse.lastStatusChange || new Date().toISOString()
              }
            : state.metadata?.guard;

        // CRITICAL FIX: Store planData and researchData from backend response
        // Frontend needs to cache this and send back as preservedPlanData/preservedResearchData on approval
        const planData = event.response?.planData || null;
        const researchData = event.response?.researchData || null;

        return {
            ...state,
            components,
            metadata: {
                ...state.metadata,
                isComplete: true,
                isStreaming: false,
                isEditingSlides: false,
                editingSlidesTotal: 0,
                editedSlidesCount: 0,
                isCreatingSlides: false,
                endTime,
                completedAt: new Date(endTime).toISOString(),
                durationSeconds,
                statusLabel: state.metadata?.statusLabel || 'Request processed',
                statusHint: null,
                guard: mergedGuard,
                // Store for plan approval flow
                preservedPlanData: planData,
                preservedResearchData: researchData
            }
        };
    },

    // orchestrator_info handler removed: backend never emits this event type.

    /**
     * Handle errors
     */
    error: (event, state) => {
        console.error('🚨 Processing error event:', {
          code: event.error?.code || event.code || 'stream_error',
        });

        const errorPayload = event.error || {};
        const message = errorPayload.message || event.message || '';

        if (errorPayload.code) {
            console.log('🚨 Structured error payload detected:', {
              code: errorPayload.code,
            });
        }

        // Check if this is a rate limit error
        const rateLimitMatch = message.match(/Rate limit exceeded\.\s*(\d+)\/(\d+)\s*requests used/i);

        if (rateLimitMatch) {
            console.log('💰 Converting rate limit error to rate limit component');

            let components = componentUpdaters.addRateLimitComponent(state.components, {
                creditsRemaining: 0,
                creditsRequired: null,
                resetsAt: null,
                message
            });
            components = componentUpdaters.settleConnectedAppWorkComponents(components, false);
            const rateLimitCompletedAt = new Date().toISOString();
            components = settleTimerComponents(components, { completedAt: rateLimitCompletedAt });

            return {
                ...state,
                components,
                aiMessageId: state.aiMessageId || generateMessageId(),
                isStreaming: false,  // Stop the stream
                isComplete: true,    // Mark as complete
                metadata: {
                    ...state.metadata,
                    isStreaming: false,
                    isComplete: true,
                    completedAt: rateLimitCompletedAt
                }
            };
        }

        // EVERY OTHER ERROR IS A TERMINAL THE USER HAS TO BE ABLE TO READ.
        //
        // This used to throw. `processEvent` catches everything a handler
        // throws and returns the state unchanged, so the throw did not
        // propagate anywhere: it only guaranteed that the reason never reached
        // the transcript. The turn then ended with no prose, no card and no
        // explanation, which is indistinguishable from a hang. The backend
        // sends its honest terminals down this path (an upstream provider that
        // produced nothing arrives here as `multi_model_no_provider_output`),
        // so it renders as a normal turn error the user can read and retry
        // from, NOT as a user interrupt: nothing here sets `isCancelled`.
        let components = componentUpdaters.markThinkingComplete(state.components);
        components = componentUpdaters.markAllComponentsComplete(components);
        components = componentUpdaters.settleConnectedAppWorkComponents(components, false);
        components = componentUpdaters.addTurnErrorComponent(components, {
            message: typeof message === 'string' && message.trim() ? message.trim() : null,
            code: errorPayload.code || event.code || null,
            retryable: errorPayload.retryable === true
        });
        const errorCompletedAt = new Date().toISOString();
        components = settleTimerComponents(components, { completedAt: errorCompletedAt });

        return {
            ...state,
            components,
            aiMessageId: state.aiMessageId || generateMessageId(),
            isStreaming: false,
            isComplete: true,
            metadata: {
                ...state.metadata,
                isStreaming: false,
                isComplete: true,
                hasError: true,
                completedAt: errorCompletedAt,
                statusLabel: null,
                statusHint: null
            }
        };
    }
};

/**
 * Main Stream Processor
 */
export const streamProcessor = {
    /**
     * Process a single event
     */
    processEvent: async (event, state, context = {}) => {
        console.log('⚡ EVENT RECEIVED:', {
          type: event.type,
          hasContent: !!event.content,
          hasPlanContent: !!(event.planContent || event.fullPlanContent),
          showButtons: event.showButtons,
          status: event.status,
          keys: Object.keys(event)
        });

        const handler = eventHandlers[event.type];

        if (!handler) {
            console.warn(`❌ Unknown event type: ${event.type}`);
            return state;
        }

        try {
            // Handle both sync and async event handlers properly
            const result = await handler(event, state, context);
            return result;
        } catch (error) {
            console.error(`Error processing ${event.type}:`, {
              name: error?.name || 'Error',
              code: error?.code || 'stream_event_failed',
            });
            // Return unchanged state on error - graceful degradation
            return state;
        }
    },

    /**
     * Initialize stream state
     */
    initializeState: (messageId = null) => ({
        aiMessageId: messageId || generateMessageId(),
        components: [],
        metadata: {
            isStreaming: true,
            startTime: Date.now(),
            endTime: null,
            durationSeconds: 0,
            hasContent: false,
            isComplete: false,
            statusLabel: 'Working…',
            statusHint: null,
            planTelemetry: {
                latest: null,
                snapshots: [],
                reminders: 0,
                autoInvokes: 0,
                queueLength: 0,
                directives: []
            },
            loop: {
                summary: null,
                steps: []
            },
            guard: {
                status: 'idle',
                satisfied: false,
                blocked: false,
                reason: null,
                lastStatusChange: null
            }
        }
    }),

    /**
     * Create message object from state
     */
    createMessage: (state) => {
        let baseComponents = Array.isArray(state.components)
            ? state.components.filter(component => component?.type !== 'timer')
            : [];

        // CRITICAL FIX: Ensure all thinking blocks are marked complete when message is complete
        // This catches any thinking blocks that might have slipped through due to race conditions
        const isMessageComplete = state.metadata?.isComplete === true || state.metadata?.isStreaming === false;
        if (isMessageComplete) {
            baseComponents = baseComponents.map(component => {
                if (component.type === 'thinking' && !component.isComplete) {
                    const endTime = Date.now();
                    const startTime = component.thinkingStartTime || endTime;
                    const durationSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));
                    return {
                        ...component,
                        thinkingEndTime: endTime,
                        thinkingDurationSeconds: durationSeconds,
                        isLoading: false,
                        isComplete: true,
                        isCollapsed: true
                    };
                }
                return component;
            });
        }

        const augmentedComponents = augmentMessageComponents({
            components: baseComponents,
            metadata: state.metadata
        });

        const timerComponent = buildTimerComponent(state);

        const components = timerComponent
            ? [timerComponent, ...augmentedComponents]
            : augmentedComponents;

        const metadataSnapshot = state.metadata
            ? JSON.parse(JSON.stringify(state.metadata))
            : undefined;

        return {
            id: state.aiMessageId,
            sender: 'ai',
            timestamp: new Date().toISOString(),
            components,
            metadata: metadataSnapshot,
            isStreaming: state.metadata?.isStreaming ?? true
        };
    },

    /**
     * Check if we should save the message
     */
    shouldSaveMessage: (state, prevState) => {
        // Quick checks first
        if (!prevState) return true;
        if (state.components.length !== prevState.components.length) return true;

        // For thinking updates, only save if content changed significantly
        if (state.components.length > 0 && state.components[0].type === 'thinking') {
            const currThinking = state.components[0];
            const prevThinking = prevState.components[0];

            if (!prevThinking || prevThinking.type !== 'thinking') return true;

            return (
                currThinking.content !== prevThinking.content ||
                currThinking.isComplete !== prevThinking.isComplete ||
                currThinking.isLoading !== prevThinking.isLoading
            );
        }

        // For other updates, do a more thorough check
        // But avoid JSON.stringify for performance
        return true; // Conservative: save other changes
    },

    isStreamComplete: (state) => {
        if (!state || !state.metadata) {
            return false;
        }
        if (state.metadata.isComplete) {
            return true;
        }
        if (state.metadata.isStreaming === false) {
            return true;
        }
        return false;
    }
};

export default streamProcessor;
